import { Row, Col, Avatar, Input, Image, Checkbox } from 'antd';
import { useNavigate } from 'react-router-dom';
import CustomModal from '../../components/Modal/CustomModal';
import ConfirmDialog from '../../components/Modal/ConfirmDialog';
import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useShell } from '../../components/Layout/ChatShell';
import PaneBack from '../../components/Layout/PaneBack';
import { useBackGuard } from '../../hooks/useBackGuard';
import MessageMenu from '../../components/ChatLayout/MessageMenu';
import MessageMenuTooltip from '../../components/ChatLayout/MessageMenuTooltip';
import DayDivider from '../../components/ChatLayout/DayDivider';
import ComposerAttachments from '../../components/ChatLayout/ComposerAttachments';
import TypingIndicator from '../../components/ChatLayout/TypingIndicator';
import MessageTicks from '../../components/ChatLayout/MessageTicks';
import PlaceholderAvatar from '../../components/ChatLayout/PlaceholderAvatar';
import { Avatar as PeerAvatar } from '../../components/ChatLayout/ListRow';
import {
  InfoHeader,
  InfoBody,
  InfoIdentity,
  InfoStatusPill,
  InfoActions,
  InfoAction,
  InfoField,
  InfoReadout,
  InfoSection,
  InfoEmpty,
  InfoButton,
  MemberRow,
  MediaGrid,
  MediaTile,
} from '../../components/ChatLayout/InfoPanel';
import './GroupMessages.css';
import { Text } from '../../components';
import { Colors, Utils } from '../../config';
import { dayDividerFor } from '../../config/util';
import { buildMentionMatcher } from '../../config/mentions';
import GroupComposer from '../../components/ChatLayout/GroupComposer';
import ReactAudioPlayer from 'react-audio-player';
import ReactPlayer from 'react-player';
import { MsgAction } from '../../store/actions';
import { useForm, Controller } from 'react-hook-form';
import { Loader } from '../../components';
// React Icons
import {
  DeleteAccImage,
  GroupImage,
  hoverNotificationsImage,
  Notifition3Image,
} from '../../assets/images';
import { CiVideoOn } from 'react-icons/ci';
import { GiSoundWaves } from 'react-icons/gi';
import { IoCallOutline, IoChevronBackCircle, IoInformationCircleSharp } from 'react-icons/io5';
import { UserOutlined } from '@ant-design/icons';
import {
  MdCancel,
  MdOutlineDelete,
  MdEdit,
  MdClose,
  MdInfoOutline,
  MdOutlineForum,
  MdContentCopy,
  MdPersonAddAlt1,
  MdCampaign,
  MdAdminPanelSettings,
  MdRemoveModerator,
  MdKey,
  MdPersonRemove,
} from 'react-icons/md';
import { IoMdArrowBack } from 'react-icons/io';
import { FaMicrophone } from 'react-icons/fa';
import { FaRecordVinyl, FaAudioDescription } from 'react-icons/fa6';
import { IoSend, IoAttach } from 'react-icons/io5';
import { useDispatch, useSelector } from 'react-redux';
import SocketAPI, { isRealtimeUsable, SOCKET_DOWN_MESSAGE } from '../../config/socket/socket';
import MessageReactions from '../../components/ChatLayout/MessageReactions';
import ReactionPicker from '../../components/ChatLayout/ReactionPicker';
import MessageText from '../../components/ChatLayout/MessageText';
import ComposerEmojiPicker from '../../components/ChatLayout/ComposerEmojiPicker';
import emojiSound from './emojiSound.mp3';
import {
  MESSAGE_CHAT_CHANNEL_DIRECT_UPDATE,
  THREADS_CHAT_CHANNEL_DIRECT_UPDATE,
} from '../../store/constants';
import REACTION_EMOJIS from '../../config/emojis';
import { applyReactionEvent } from '../../config/util/reactions';
import {
  CHANNEL_KIND,
  GROUP_LIMITS,
  LOGIN_ID_DIGITS,
  MEMBER_ROLE,
  PRESENCE_RECOUNT_MS,
  REPORT_LIMITS,
  ROUTES,
  TYPING_EXPIRY_MS,
  TYPING_IDLE_MS,
} from '../../config/constants';
import { useDarkModeValue } from '../../hooks/useDarkModeValue';
import { useSharedMedia } from '../../hooks/useSharedMedia';
import {
  useInfiniteList,
  useScrollAnchor,
  useStreamBottomOnOpen,
} from '../../hooks/useInfiniteList';
import { usePresence, PRESENCE } from '../../hooks/usePresence';
import { useGroupCall } from '../../hooks/useGroupCall';

/**
 * Attachment cap, enforced in the effects that watch each queue. The upload
 * handlers also reject beyond this, so the constant is shared rather than
 * repeated as a literal in five places.
 */
const MAX_ATTACHMENTS = 5;

/**
 * Longest video that can be attached, in seconds. Enforced against the clip's
 * own metadata once the browser has read it.
 */
const MAX_VIDEO_SECONDS = 60;

/**
 * Overlapping avatars of the people who replied in a thread.
 *
 * Replaces antd's `Avatar.Group`, which clones its children to size and stack
 * them and therefore only works with antd's own Avatar — so the app's Avatar
 * (initials, then the placeholder mark) could not be used inside it, and the
 * fallback had to be `randoming.png`: a hooded-figure raster, fixed dark on
 * white, unable to follow the theme and reading as "hacker" rather than "we do
 * not know who this is". The overlap is `.avatar-stack` in index.css.
 */
const ReplierStack = ({ repliers, className = '' }) => (
  <span className={`avatar-stack ${className}`}>
    {repliers.slice(0, 2).map((user, index) => (
      <PeerAvatar
        key={user?.id ?? index}
        size={22}
        name={user?.first_name}
        seed={user?.id}
        src={user?.media?.media_url || user?.media?.media_thumb_url}
      />
    ))}
  </span>
);

/**
 * How a roster member is addressed.
 *
 * A secret group's rows carry only `member_mode` — the pseudonym — and the real name
 * must never be reached for there. Extracted so the row label and every confirmation
 * prompt name the same person the same way.
 */
const memberDisplayName = (person) =>
  person?.user?.nick_name ||
  (person?.member_mode === 'Username' ? person?.user?.first_name : person?.member_mode) ||
  'this member';

/**
 * The four roster actions, and what to tell someone before they commit to one.
 *
 * `confirm` is deliberately specific about the consequence rather than asking "are you
 * sure": a transfer cannot be undone by the person performing it, and a removal is not
 * obviously reversible unless you know the history survives it.
 */
const MEMBER_ACTIONS = {
  promote: {
    title: 'Make admin',
    confirmLabel: 'Make admin',
    body: (name) =>
      `${name} will be able to add and remove members, edit the group, and post in a broadcast channel.`,
  },
  demote: {
    title: 'Remove admin',
    confirmLabel: 'Remove admin',
    body: (name) => `${name} goes back to being an ordinary member of this group.`,
  },
  transfer: {
    title: 'Transfer ownership',
    confirmLabel: 'Transfer',
    danger: true,
    body: (name) =>
      `${name} becomes the owner and you stay on as an admin. Only an owner can transfer ownership, so you will not be able to take it back yourself.`,
  },
  remove: {
    title: 'Remove member',
    confirmLabel: 'Remove',
    danger: true,
    body: (name) =>
      `${name} loses access to this group. Their messages stay in the history, and their unread state is kept in case they are added back.`,
  },
};

/**
 * The roster row's actions, in the `trailing` slot MemberRow has always had.
 *
 * Presentational only: it renders whichever of the four `permissions` are true and
 * reports the choice upward. Nothing is disabled — an action this viewer may not take
 * is absent, because a greyed-out "Transfer ownership" on every row would advertise
 * authority most members do not have. Icon-only with `title`/`aria-label`, since four
 * labelled buttons do not fit a 320px panel beside a name.
 */
const MemberActions = ({ permissions, name, onAction }) => {
  const items = [
    { kind: 'promote', label: `Make ${name} an admin`, icon: <MdAdminPanelSettings size={17} /> },
    { kind: 'demote', label: `Remove ${name} as admin`, icon: <MdRemoveModerator size={16} /> },
    { kind: 'transfer', label: `Transfer ownership to ${name}`, icon: <MdKey size={16} /> },
    { kind: 'remove', label: `Remove ${name} from the group`, icon: <MdPersonRemove size={17} /> },
  ].filter((item) => permissions[item.kind]);

  if (!items.length) return null;

  return (
    <span className="member-actions">
      {items.map((item) => (
        <button
          key={item.kind}
          type="button"
          className={`member-action ${item.kind === 'remove' ? 'is-danger' : ''}`}
          title={item.label}
          aria-label={item.label}
          onClick={() => onAction(item.kind)}
        >
          {item.icon}
        </button>
      ))}
    </span>
  );
};

/** Stable empty stream — see `GroupChatMsgs`. */
const EMPTY_STREAM = [];

const GroupMessages = () => {
  const { isPhone, rightSideOpen1, setRightSideOpen1, rightSideOpen2, setRightSideOpen2 } =
    useShell();

  /**
   * On a phone the thread column and the details panel each cover the whole
   * conversation, so Back has to peel one layer rather than leave the group.
   *
   * One guard for both panels, not one each. The two are mutually exclusive —
   * opening a thread closes the details panel in the same render — and a guard
   * per panel would unwind and re-push its history entry mid-switch. This one
   * stays armed across the switch and only its handler changes, which also
   * matches the panels' own back button: a thread returns to the details it was
   * opened from, and the details close.
   */
  const openPanelLayer = rightSideOpen1 ? 'thread' : rightSideOpen2 ? 'details' : null;

  useBackGuard(isPhone && openPanelLayer, () => {
    if (rightSideOpen1) {
      setRightSideOpen1(false);
      setRightSideOpen2(true);
      return;
    }
    setRightSideOpen2(false);
  });
  const socket = SocketAPI();
  const { isDarkMode } = useDarkModeValue();
  // use Dispatch
  const dispatch = useDispatch();

  //use Navigate
  const navigate = useNavigate();

  // use Ref
  const lastMessageRef = useRef(null);
  const ScrollingRef = useRef(null);
  const ringtoneRef = useRef(null);
  //Threads
  const lastThreadMessageRef = useRef(null);
  const threadScrollingRef = useRef(null);
  const [elapsedThreadTime, setElapsedThreadTime] = useState(0); // Time in seconds
  const intervalThreadRef = useRef(null);
  /**
   * The live microphone capture for a thread voice note, so it can be released on the
   * way out. The recorder's `onstop` releases it on the normal path; this covers the
   * one it cannot — closing the thread, or leaving the group, while still recording,
   * where `stop()` is never called and `onstop` therefore never fires.
   */
  const threadStreamRef = useRef(null);

  //use Selector
  const userData = useSelector((state) => state?.AppReducer?.user?.data);
  /**
   * One subscription per value, not one for the whole slice.
   *
   * This was `useSelector((state) => state.MsgReducer)` destructured into the fourteen
   * names below. That selector returns the slice OBJECT, so react-redux's reference
   * check fired whenever any of its ~60 keys changed — not just these fourteen. A
   * notification arriving, a call-history page landing, an unread-count tick, a
   * shared-media fetch or a saved-message toggle each forced a full re-render of this
   * 4,700-line component and every message bubble under it.
   *
   * The two cursor selectors below this block already did it correctly; this brings the
   * rest into line.
   */
  const selected_chat_user = useSelector((state) => state?.MsgReducer?.selected_chat_user);
  const message_chats = useSelector((state) => state?.MsgReducer?.message_chats);
  const message_chats_loader = useSelector((state) => state?.MsgReducer?.message_chats_loader);
  const threads_chats_loader = useSelector((state) => state?.MsgReducer?.threads_chats_loader);
  // Add-members-by-ID search; set by CREATE_GROUP_ADD_ID.
  const group_loader = useSelector((state) => state?.MsgReducer?.group_loader);
  const thread_details = useSelector((state) => state?.MsgReducer?.thread_details);
  const upload_thread_media = useSelector((state) => state?.MsgReducer?.upload_thread_media);
  const upload_thread_loader = useSelector((state) => state?.MsgReducer?.upload_thread_loader);
  const threads_chats = useSelector((state) => state?.MsgReducer?.threads_chats);
  const message_chats_channel_id = useSelector(
    (state) => state?.MsgReducer?.message_chats_channel_id
  );
  const threads_chats_channel_id = useSelector(
    (state) => state?.MsgReducer?.threads_chats_channel_id
  );
  const create_thread_loader = useSelector((state) => state?.MsgReducer?.create_thread_loader);
  const search_by_id = useSelector((state) => state?.MsgReducer?.search_by_id);
  const group_members = useSelector((state) => state?.MsgReducer?.group_members);
  const threads_listing = useSelector((state) => state?.MsgReducer?.threads_listing);
  const loader = useSelector((state) => state?.AppReducer?.loader);
  const AddUserById = search_by_id?.data;
  const SelectedChatGroup = selected_chat_user;

  /**
   * Each stream, but only when it is the one on screen.
   *
   * `selected_chat_user` moves on the sidebar click and the stream is cleared on the
   * route commit after it, so for one commit the group id is the group being opened
   * while the rows are still the group being left — which is what made every switch
   * after the first open on old messages and stop paging (see `message_chats_channel_id`
   * in the reducer). A thread is the same shape: `ClearChatThreads` and the fetch are
   * dispatched together, so a reopened panel briefly holds the previous thread's replies.
   */
  const groupStreamIsCurrent =
    Boolean(SelectedChatGroup?.id) &&
    String(message_chats_channel_id) === String(SelectedChatGroup?.id);
  const threadStreamIsCurrent =
    Boolean(thread_details?.id) && String(threads_chats_channel_id) === String(thread_details?.id);

  const GroupChatMsgs = groupStreamIsCurrent ? message_chats : EMPTY_STREAM;
  const ThreadsMsgs = threadStreamIsCurrent ? threads_chats : EMPTY_STREAM;

  /**
   * When every OTHER live member had read everything up to — the timestamp a double tick
   * means. Server-computed as the minimum `last_read_at` across the other members, so a
   * single straggler keeps it at one tick.
   *
   * Keyed by channel in the store because a group and an open thread are two channels with
   * two independent memberships, both on screen at once. Seeded from each stream's history
   * read, so ticks survive a reload rather than existing only for as long as the pane is
   * mounted.
   */
  const othersReadAtByChannel = useSelector((state) => state?.MsgReducer?.others_read_at);

  /**
   * Day-separator label per thread reply, keyed by message id; absent for a reply
   * that continues the day of the one above it.
   *
   * Precomputed rather than worked out in the render, because the thread column
   * DROPS deleted replies where the main stream tombstones them. The comparison
   * therefore has to be against the reply actually rendered above, which the
   * neighbouring array element is not — a day whose opening replies had all been
   * deleted would compare against one of them, match, and lose its separator.
   *
   * Keyed by id rather than returning a filtered list, so the render keeps using
   * raw indices: `DeleteThreadMessageNow` addresses a reply by its position in
   * the UNFILTERED list, and handing it a filtered index would tombstone a
   * different message than the one whose menu was used.
   */
  const threadDayLabels = useMemo(() => {
    const labels = new Map();
    let previousCreatedAt;

    (ThreadsMsgs ?? []).forEach((item) => {
      if (item?.deleted_by_id != null) return;
      const label = dayDividerFor(item?.createdAt, previousCreatedAt);
      if (label) labels.set(item?.id, label);
      previousCreatedAt = item?.createdAt;
    });

    return labels;
  }, [ThreadsMsgs]);

  // Server cursors — `null` after a load means there is nothing older. `message_count`
  // and `thread_count` are advisory now; they were the paging guard, and a total
  // compared against a page length is what let the stream re-request past the end
  // forever once the reader reached the beginning.
  const messageNextCursor = useSelector((state) => state?.MsgReducer?.message_next_cursor);
  const threadNextCursor = useSelector((state) => state?.MsgReducer?.thread_next_cursor);

  const captureGroupAnchor = useScrollAnchor(ScrollingRef, GroupChatMsgs?.length);
  const captureThreadAnchor = useScrollAnchor(threadScrollingRef, ThreadsMsgs?.length);

  // Opens each stream on its newest message. This was a pair of `useRef(false)` latches
  // that were never reset per conversation, so the scroll fired for the first group
  // opened in a session and for no group opened after it. Keyed on the conversation, so
  // every open is armed and no prepended page is.
  useStreamBottomOnOpen(ScrollingRef, {
    key: SelectedChatGroup?.id,
    count: GroupChatMsgs?.length ?? 0,
  });
  useStreamBottomOnOpen(threadScrollingRef, {
    // `thread_details` rather than the `ThreadDetails` alias — that alias is declared
    // below this point.
    key: thread_details?.id,
    count: ThreadsMsgs?.length ?? 0,
  });
  const ThreadImagesUrl = upload_thread_media?.data?.chatMedia;
  const uploadThreadLoader = upload_thread_loader;
  const ThreadDetails = thread_details;
  // Fixed set of unicode reaction emojis (see config/emojis) — each entry is the
  // emoji character itself, stored directly in chat_reactions.emoji_id.
  const EmojiLists = REACTION_EMOJIS;
  const GroupDetails = group_members?.data?.rows?.[0];

  // A secret group hides who is in it. Every message in one already renders as
  // "Anonymous"; listing the roster in the details panel handed back the very
  // identities the mode exists to withhold.
  const isSecretGroup = Boolean(SelectedChatGroup?.is_secret);

  // ── Channel permissions ─────────────────────────────────────────────────────
  //
  // Every one of these comes from the SERVER (`GET /chat/group/:id` returns
  // channel_kind, my_role, can_post, can_reply, can_administer). They are never
  // derived from is_broadcast plus a role here: the client would drift from the
  // rules the moment they change, and it is the server that refuses the write
  // either way. `GroupDetails` is the authority; `SelectedChatGroup` is the
  // sidebar row and may be a page older, so it is only a fallback.
  const isChannel =
    (GroupDetails?.channel_kind ?? SelectedChatGroup?.channel_kind) === CHANNEL_KIND.CHANNEL ||
    Boolean(GroupDetails?.is_broadcast ?? SelectedChatGroup?.is_broadcast);

  // Default to permitting in a normal group (unchanged behaviour) and to refusing
  // in a channel until the server has actually said otherwise — an undefined
  // `can_post` must never read as "yes" for a broadcast channel.
  const canPost = GroupDetails?.can_post ?? !isChannel;
  const canReply = (GroupDetails?.can_reply ?? !isChannel) && canPost;
  const canAdministerChannel = Boolean(GroupDetails?.can_administer);
  const myChannelRole = GroupDetails?.my_role ?? MEMBER_ROLE.MEMBER;

  // Ownership cannot simply be walked away from: `leaveGroup` refuses an OWNER until
  // it has been handed over, because nothing else in the channel confers authority
  // and an orphaned group can never be edited or administered again.
  const mustTransferBeforeLeaving = myChannelRole === MEMBER_ROLE.OWNER;

  /**
   * Which roster actions this viewer may take on this member.
   *
   * Each line mirrors the rule its own endpoint enforces, so the button is absent
   * exactly when the write would be refused — see `promoteChannelAdmins`,
   * `demoteChannelAdmin`, `transferChannelOwnership` and `removeChannelMember` in
   * the backend's chat.service.js. Roles come from the membership row the server
   * sends (`member_role`, `my_role`), never from `start_from_user_id`: that is the
   * creator, which misses promoted admins and points at the wrong person after a
   * transfer.
   */
  const memberPermissions = (person) => {
    const viewerIsOwner = myChannelRole === MEMBER_ROLE.OWNER;
    const targetRole = person?.member_role ?? MEMBER_ROLE.MEMBER;
    const targetIsOwner = targetRole === MEMBER_ROLE.OWNER;
    const targetIsAdmin = targetRole === MEMBER_ROLE.ADMIN;
    const self = Boolean(person?.is_self);

    return {
      // OWNER/ADMIN may promote any non-owner. Promoting an existing admin is a
      // no-op server-side, so it is not offered.
      promote: canAdministerChannel && !self && !targetIsOwner && !targetIsAdmin,
      // Owner-only — letting admins demote each other turns the roster into a race.
      // The owner is never demotable; ownership moves by transfer instead.
      demote: viewerIsOwner && targetIsAdmin,
      // Owner-only. The target need not already be an admin, and the outgoing owner
      // stays on as one — a transfer is not a departure.
      transfer: viewerIsOwner && !self,
      // An admin cannot remove a peer admin: only the owner outranks one. Self-removal
      // is Leave group, which the server says so explicitly.
      remove: canAdministerChannel && !self && !targetIsOwner && (!targetIsAdmin || viewerIsOwner),
    };
  };

  // `/chat/threads` is asked for THIS group specifically (`group_id`), so the response
  // is the one row this panel wants. It used to fetch every group the reader belongs to
  // and pick the matching row here — which is why the request carried an absurd limit:
  // any real page size risked the open group not being on the page, and the panel then
  // showed "no threads" for a group that had them.
  const GroupThreads =
    (threads_listing || []).find((row) => String(row?.id) === String(SelectedChatGroup?.id))
      ?.threads || [];

  //use State
  const [modal, setModal] = useState(false);
  const [editGroup, setEditGroup] = useState(false);
  const [deleteThreadModal, setDeleteThreadModal] = useState(false);
  // The message a "delete for everyone" confirmation is pending on, or null. Holds the
  // id rather than a boolean because the prompt names what is about to be withdrawn.
  const [retractTarget, setRetractTarget] = useState(null);
  const [leaveGroupModal, setLeaveGroupModal] = useState(false);
  // The pending roster action — `{ kind, person }` — or null. Holds the member rather
  // than a boolean per action because every one of these prompts names who it is about.
  const [memberAction, setMemberAction] = useState(null);
  const [addMembersModalOpen, setAddMembersModalOpen] = useState(false);
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [membersListModal, setMembersListModal] = useState(false);
  const [membersList, setMembersList] = useState(null);
  const [reactedListModal, setReactedListModal] = useState(false);
  const [reactedMembersList, setReactedMembersList] = useState(null);
  const [reportMsgId, setReportMsgId] = useState(null);
  // Boolean, not the string 'show' it used to hold — it drives an
  // `aria-expanded` and a `!` toggle, and a truthy string breaks both.
  const [showHideMedia, setShowHideMedia] = useState(true);
  const [showHideMembers, setShowHideMembers] = useState(true); // Show and Hide Media
  const [showHideThreads, setShowHideThreads] = useState(true);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedImagesThread, setSelectedImagesThread] = useState([]);
  const [typing, setTyping] = useState();
  const [ismodalopen, setIsmodalopen] = useState();
  const [newMessageRecieve, setNewMessageRecieve] = useState(false);
  const [newMessageRecieveThread, setNewMessageRecieveThread] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [threadMessageInput, setThreadMessageInput] = useState('');
  const [replyOnMsg, setReplyOnMsg] = useState(null);
  const [replyOnMsgThread, setReplyOnMsgThread] = useState(null);
  const [isMembers, setIsMembers] = useState(false);
  const [isMembersThread, setIsMembersThread] = useState(false);
  const [isRecordingThread, setIsRecordingThread] = useState(false); // Track recording state
  const [mediaRecorderThread, setMediaRecorderThread] = useState(null); // Store MediaRecorder instance
  const [audioUrlThread, setAudioUrlThread] = useState(null);
  const audioChunksThread = useRef([]);
  const [isChecked, setIsChecked] = useState(false);
  const [unreadMsg, setUnreadMsg] = useState(0);
  const [, setThreadDataDelete] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [imageURL, setImageURL] = useState(SelectedChatGroup?.media?.media_url); //Image
  const [file, setFile] = useState(null);
  const [secretGroupValue, setSecretGroupValue] = useState(false);

  // A mention is an attribution — it says a named person addressed you — which is
  // the one thing a secret group exists to withhold. So the picker is not offered
  // here at all, and the server drops any `mentionIds` a stale client still sends.
  const mentionsEnabled = !isSecretGroup;

  // Drives the @mention autocomplete, so it excludes you: the roster now includes
  // the viewer (to show a "(You)" row in the details panel) and there is no point
  // offering to mention yourself — the server's mention fan-out skips the sender
  // anyway, so the entry would silently do nothing. Members with no display name
  // are dropped rather than listed as a blank row you cannot resolve.
  const convertedGroupUsers = useMemo(() => {
    if (!mentionsEnabled) return [];
    return (SelectedChatGroup?.members ?? [])
      .filter((member) => !member?.is_self && member?.user?.id !== userData?.id)
      .map((member) => ({
        id: member.user?.id,
        name: member.user?.first_name,
        image: member.user?.media?.media_url,
      }))
      .filter((member) => member.id && member.name);
  }, [mentionsEnabled, SelectedChatGroup?.members, userData?.id]);

  // Names the bubbles highlight. The viewer is included here — unlike the picker —
  // because a mention of the reader is the one worth making visible in the stream.
  const mentionNames = useMemo(() => {
    if (!mentionsEnabled) return [];
    const names = convertedGroupUsers.map((member) => member.name);
    if (userData?.first_name) names.push(userData.first_name);
    return names;
  }, [mentionsEnabled, convertedGroupUsers, userData?.first_name]);

  /**
   * The roster prepared once per group, not once per bubble.
   *
   * Every `MessageText` in the stream used to receive `mentionNames` and re-derive the
   * sorted, de-duplicated, lower-cased candidate list itself. That is O(roster) work
   * per message per render — so in a large channel one keystroke in the composer re-sorted
   * the roster once for every message on screen.
   */
  const matchMentions = useMemo(() => buildMentionMatcher(mentionNames), [mentionNames]);

  const channel_id = SelectedChatGroup?.id;
  const thread_channel_id = ThreadDetails?.id;

  /**
   * Whether someone ELSE in this group is typing.
   *
   * `typing:alert` is broadcast per channel and echoes back to the sender, so
   * all three guards carry weight: the flag must be live, it must belong to the
   * channel on screen, and it must not be this user's own keystrokes.
   */
  const isPeerTyping = Boolean(
    typing?.status && typing?.channel_id === channel_id && typing?.sender_id !== userData?.id
  );

  /**
   * One place that announces typing, for both the group and its threads.
   *
   * The channel is a parameter because a thread is a channel of its own — the
   * three call sites used to inline the same payload with a different id, and the
   * pseudonym rule (`member_mode`) with it, which is exactly the kind of thing
   * that gets fixed in two places out of three.
   */
  const emitTyping = useCallback(
    (channelId, status) => {
      if (!channelId) return;
      socket?.emit('typing', {
        channel_id: channelId,
        status,
        name:
          SelectedChatGroup?.chat_member?.member_mode == 'Username'
            ? userData?.first_name
            : SelectedChatGroup?.chat_member?.member_mode,
        user_id: userData?.id,
      });
    },
    [socket, SelectedChatGroup?.chat_member?.member_mode, userData?.first_name, userData?.id]
  );

  // Separate debounce timers: the group and the thread are separate channels, so
  // a keystroke in one must not cancel the other's pending stop.
  const typingStopTimer = useRef(null);
  const threadTypingStopTimer = useRef(null);

  // Stop typing in both channels on the way out — on unmount and whenever the
  // group changes, so leaving mid-sentence does not leave this user shown as
  // still typing in the group they left.
  useEffect(
    () => () => {
      clearTimeout(typingStopTimer.current);
      clearTimeout(threadTypingStopTimer.current);
      emitTyping(channel_id, false);
      emitTyping(thread_channel_id, false);
    },
    [emitTyping, channel_id, thread_channel_id]
  );

  /**
   * Last resort for the thread's microphone.
   *
   * Unmounting mid-recording does not stop the recorder, so its `onstop` — where the
   * tracks are normally released — never runs, and the capture stays open with the
   * browser's recording indicator lit. Empty deps: this must fire on unmount only, not
   * on every group change, or it would kill a recording still in progress.
   */
  useEffect(
    () => () => {
      clearInterval(intervalThreadRef.current);
      threadStreamRef.current?.getTracks().forEach((track) => track.stop());
      threadStreamRef.current = null;
    },
    []
  );

  // Shared media for the details panel: the first page when it opens, further
  // pages as it is scrolled. The hand-rolled version this replaces could fire a
  // burst of identical requests per scroll gesture and then sit on a spinner
  // that never resolved — see the hook for why.
  const {
    media: shareMedia,
    onScroll: handleMediaScroll,
    loading: shareMediaLoading,
  } = useSharedMedia({ channelId: channel_id, open: rightSideOpen2 });

  /**
   * The scrollable member list.
   *
   * Separate from `GroupDetails.members`, which is the bounded roster the group payload
   * carries and is what the header count and the permission checks read. `enabled` is
   * tied to the panel being open, so a closed details panel neither fetches nor
   * observes.
   */
  const roster = useSelector((state) => state?.MsgReducer?.channel_members) ?? [];
  const rosterLoading = useSelector((state) => state?.MsgReducer?.channel_members_loader);
  const rosterRef = useRef(roster);
  rosterRef.current = roster;

  /**
   * Live presence for the members on screen.
   *
   * The roster's dots came from `user_online_status` stamped on the row by the HTTP read
   * and never moved again, so a details panel left open showed who had been online when it
   * loaded. Subscribing to the loaded page keeps them live; the server refuses ids the
   * viewer is not entitled to watch (a secret group's roster is withheld entirely, so
   * there is nothing to subscribe to there in the first place).
   */
  const rosterMemberIds = useMemo(
    () => roster.map((person) => person?.user?.id).filter(Boolean),
    [roster]
  );
  const { statusOf: presenceStatusOf } = usePresence(socket, rosterMemberIds);

  /**
   * Is this group already on a call? If so the call buttons JOIN it rather than starting
   * a new one — see the group branch of `generateToken`, which returns the live call
   * instead of minting a second channel.
   */
  const { activeCall: activeGroupCall } = useGroupCall(socket, SelectedChatGroup?.id);

  const memberStatus = useCallback(
    (person) => presenceStatusOf(person?.user?.id) ?? person?.user?.user_online_status,
    [presenceStatusOf]
  );

  const fetchRoster = useCallback(
    (cursor) =>
      dispatch(
        MsgAction.ChannelMembersPage({
          channel_id: SelectedChatGroup?.id,
          offset: cursor === null ? 0 : rosterRef.current.length,
        })
      ),
    [dispatch, SelectedChatGroup?.id]
  );

  const { sentinelRef: rosterSentinelRef, exhausted: rosterExhausted } = useInfiniteList({
    resetKey: SelectedChatGroup?.id,
    fetchPage: fetchRoster,
    loading: rosterLoading,
    loadedCount: roster.length,
    nextCursor: undefined,
    enabled: Boolean(rightSideOpen2 && SelectedChatGroup?.id && !SelectedChatGroup?.is_secret),
  });

  // use Form
  const { handleSubmit } = useForm({});
  const {
    handleSubmit: handleSubmitReport,
    control: controlReport,
    reset,
  } = useForm({
    mode: 'onChange',
  });

  const {
    handleSubmit: handleSubmitMessage,
    control: controlMessage,
    reset: resetMessage,
  } = useForm({
    mode: 'onChange',
  });
  const {
    handleSubmit: handleSubmitEdit,
    control: controlEdit,
    reset: resetEdit,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      groupName: SelectedChatGroup?.name,
      description: SelectedChatGroup?.description,
    },
  });

  // Enter-to-send belongs to each composer, and both <Input>s already own it via
  // `onPressEnter` — with the guards they need (`canSend` / `canSendThread`, not
  // uploading, not recording). Two `keydown` listeners on `document` used to be
  // registered here as well (this effect and a second one with `[]` deps), which
  // caused three separate problems:
  //
  //   · Three sends per Enter — both document listeners plus the input's own
  //     handler, all firing for one keystroke.
  //   · A thread reply on EVERY Enter, anywhere: the thread guard read
  //     `selectedImagesThread || …`, and an empty array is truthy.
  //   · Attachments wiped while typing: the handler cleared both image queues
  //     outside its own `if (event.key === 'Enter')` check, so any keystroke
  //     emptied them. The real clearing lives in the send callbacks, which reset
  //     input, attachments, reply and audio once the server acks.
  /**
   * Zero a channel's unread count and refresh the sidebar badge plus the nav
   * total. The emit used to fire without an ack, so nothing on screen was
   * refreshed and the badge kept its stale value.
   */
  const markChannelRead = useCallback(
    (id) => {
      if (!id || !userData?.id) return;
      // A group left open in a background tab is not being read — see the
      // visibilitychange effect below, which flushes the read on return.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      // A parked connection (rejected at the handshake, nothing scheduled to retry) never
      // flushes its buffer and `emit` reports nothing, so the ack never came, the badge
      // stayed put and the other members never got the receipt.
      if (!isRealtimeUsable(socket)) return;

      socket.emit('message:read', { channel_id: id }, (ack) => {
        // The server refuses a read for a channel the caller is not in. This cleared the
        // badge regardless.
        if (ack && ack.success === false) return;
        // Was `GlobalSearchChatsGroups()` — no arguments, so unfiltered page one, which
        // wiped the sidebar's active search and every page it had scrolled through. And
        // it ran again on every inbound message for as long as the group stayed open.
        dispatch(
          MsgAction.ChannelRead({
            channel_id: id,
            others_read_at: ack?.others_read_at ?? undefined,
          })
        );
        dispatch(MsgAction.UnreadMessageDot());
      });
    },
    [socket, userData?.id, dispatch]
  );

  useEffect(() => {
    markChannelRead(channel_id);
    markChannelRead(thread_channel_id);
  }, [markChannelRead, channel_id, thread_channel_id]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      markChannelRead(channel_id);
      markChannelRead(thread_channel_id);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [markChannelRead, channel_id, thread_channel_id]);

  useEffect(() => {
    if (selectedImages.length > 0) {
      const formData = new FormData();
      for (const key in selectedImages) {
        formData.append('media', selectedImages[key]);
      }
      dispatch(MsgAction.UploadChatMedia(formData));
    }
  }, [selectedImages]);

  useEffect(() => {
    if (selectedImagesThread.length > 0) {
      const formData = new FormData();
      for (const key in selectedImagesThread) {
        formData.append('media', selectedImagesThread[key]);
      }
      dispatch(MsgAction.UploadThreadMedia(formData));
    }
  }, [selectedImagesThread]);

  /**
   * Latest streams, readable from a socket handler without being a dependency of it.
   *
   * Both reaction effects used to list the message array in their dependencies, so
   * that the handler could close over it. Every inbound message produces a new array,
   * which meant every message in a busy channel tore down and re-attached two
   * listeners on the shared socket — and the handler's view of the stream was already
   * one render stale by the time it fired.
   *
   * Assigned during render rather than in an effect: the handler must see the newest
   * array even if a reaction lands before the commit's effects have run.
   */
  const groupMsgsRef = useRef(GroupChatMsgs);
  groupMsgsRef.current = GroupChatMsgs;
  const threadMsgsRef = useRef(ThreadsMsgs);
  threadMsgsRef.current = ThreadsMsgs;

  useEffect(() => {
    if (!socket) return undefined;

    const onGroupReaction = (data) => {
      const held = groupMsgsRef.current;
      if (!held?.length) return;
      dispatch({
        type: MESSAGE_CHAT_CHANNEL_DIRECT_UPDATE,
        payload: applyReactionEvent(held, data),
      });
    };

    socket.on('emoji:receive', onGroupReaction);
    // Never removed before the ref refactor, so handlers accumulated one per
    // conversation opened — after switching chats five times a single reaction was
    // applied five times. Deps are stable now, so this subscribes once per socket.
    return () => socket.off('emoji:receive', onGroupReaction);
  }, [socket, dispatch]);

  useEffect(() => {
    if (!socket) return undefined;

    const onThreadReaction = (data) => {
      const held = threadMsgsRef.current;
      if (!held?.length) return;
      dispatch({
        type: THREADS_CHAT_CHANNEL_DIRECT_UPDATE,
        payload: applyReactionEvent(held, data),
      });
    };

    socket.on('emoji:receive', onThreadReaction);
    return () => socket.off('emoji:receive', onThreadReaction);
  }, [socket, dispatch]);

  const setNewReactionMessages = (message) => {
    dispatch({ type: MESSAGE_CHAT_CHANNEL_DIRECT_UPDATE, payload: message });
  };
  const debounceHandler = (query, delay) => {
    let timeout;
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      dispatch(MsgAction.CreateGroupAddId(query));
    }, delay);
    return timeout;
  };

  const checkKeyDown = (e) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  /**
   * Employee-ID lookup.
   *
   * A login ID is exactly nine digits, so anything else cannot match one. The cap
   * is applied here rather than with `maxLength` because that attribute is inert
   * on `type="number"` — the field accepted arbitrarily long input, and a paste
   * could put letters or a minus sign into it.
   */
  const searchHandler = (event) => {
    const query = (event?.target?.value ?? '').replace(/\D/g, '').slice(0, LOGIN_ID_DIGITS);
    setInputValue(query);
    setIsChecked(false);
    debounceHandler(query, 2000);
  };
  const setNewReactionThreads = (message) => {
    dispatch({ type: THREADS_CHAT_CHANNEL_DIRECT_UPDATE, payload: message });
  };

  /**
   * Toggle the current user's reaction on a message.
   *
   * `messages`/`commit` differ only between the group stream and the thread
   * stream; the rest was copy-pasted into all four bubble branches. A user holds
   * at most one reaction, so re-picking the same emoji clears it (`emoji_id:
   * null` is the server's remove signal) and picking another replaces it.
   */
  const toggleReaction = (msg, emoji, messages, commit) => {
    handlePlaySound();

    const existingReaction = msg?.chat_reactions?.find(
      (reaction) => String(reaction?.user_id) === String(userData?.id)
    );
    const isRemoval = existingReaction?.emoji_id === emoji;
    const emojiIdToSend = isRemoval ? null : emoji;

    socket?.emit(
      'emoji:send',
      {
        senderId: msg?.sender_id,
        channel_id: msg?.channel_id,
        message_id: msg?.id,
        emoji_id: emojiIdToSend,
        isSecret: SelectedChatGroup?.is_secret,
      },
      (resp) => {
        // Fold the ack through the same reducer as the broadcast: the server
        // returns the message's whole reaction set, so the reactor's own pane
        // lands on exactly the state every other member is shown. Rebuilding it
        // locally from `reactionsWithoutMine` was how the sender's view drifted
        // from everyone else's once two people reacted at once.
        commit(applyReactionEvent(messages, resp?.response));
      }
    );
    setCurrentIndex(-1);
  };

  /**
   * Sender avatar / name for a message.
   *
   * `user` comes from the history API; socket-delivered messages carry `sender`
   * instead, so live messages showed the fallback avatar until a reload. The name
   * already had this fallback — the photo did not.
   */
  const senderAvatar = (msg) =>
    msg?.user?.media?.media_url || msg?.sender?.media?.media_url || null;

  const senderName = (msg) =>
    (msg?.user?.first_name || msg?.sender?.first_name || '')?.replace(/\b\w/g, (char) =>
      char.toUpperCase()
    );

  const applyReaction = (msg, emoji) =>
    toggleReaction(msg, emoji, GroupChatMsgs, setNewReactionMessages);

  const applyThreadReaction = (msg, emoji) =>
    toggleReaction(msg, emoji, ThreadsMsgs, setNewReactionThreads);

  const onCheckboxChange = (person, checked) => {
    const updatedPeople = checked
      ? [...selectedPeople, person]
      : selectedPeople.filter((p) => p.id !== person.id);

    setSelectedPeople(updatedPeople);
  };

  const handleCheckboxChange = (event, user) => {
    if (event.target.checked) {
      setIsChecked(true);
      setSelectedPeople([...selectedPeople, user]);
    } else {
      setSelectedPeople(selectedPeople.filter((u) => u.id !== user.id));
    }
  };

  const MembersListClose = () => {
    setMembersListModal(false);
    setMembersList(null);
  };

  const reactedMembersListClose = () => {
    setReactedListModal(false);
    setReactedMembersList(null);
  };

  const membersListingModal = (members) => {
    setMembersListModal(true);
    setMembersList(members);
  };

  const reactedListingModal = (members) => {
    setReactedListModal(true);
    setReactedMembersList(members);
  };

  // The first-open jump lives in `useStreamBottomOnOpen` above; this one follows an
  // arrival.
  useEffect(() => {
    lastMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [newMessageRecieve]);

  /**
   * Follow the typing bubble.
   *
   * It mounts at the tail of the stream, below the newest message, and nothing
   * scrolled for it — the effects above are keyed on a message arriving — so it
   * landed just off the bottom of the viewport.
   *
   * Only when the reader is already at the end. The check runs after the bubble
   * is in the DOM, so `scrollHeight` already counts it; the 200px allowance
   * covers the bubble's own height while staying far short of "reading history",
   * which must never be interrupted for an indicator.
   */
  useEffect(() => {
    if (!isPeerTyping) return;

    const node = ScrollingRef.current;
    if (!node) return;

    const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
    if (distanceFromBottom > 200) return;

    lastMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [isPeerTyping]);

  useEffect(() => {
    lastThreadMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [newMessageRecieveThread]);

  useEffect(() => {
    setUnreadMsg(SelectedChatGroup?.chat_member?.unread_message_count);
  }, [SelectedChatGroup?.chat_member?.unread_message_count]);

  /**
   * Join the group's room, and the open thread's.
   *
   * Rooms are per CONNECTION, so this runs when the pane opens and again after
   * any reconnect. Subscribing to `connect` alone — which is all this did — only
   * worked when a full page load happened to put the connect after the listener:
   * the connection is opened once at app boot (App.js), so opening a group by
   * client-side navigation joined nothing and the pane received no live messages
   * until the reader refreshed.
   */
  useEffect(() => {
    if (!socket || !channel_id) return undefined;

    // One handler joining both channels. This was two separate `on('connect')`
    // registrations against a single `off('connect')`, so one of them leaked on
    // every remount. The teardown also called `off('join')`, which removed
    // nothing — `join` is emitted, never subscribed.
    const join = () => {
      socket.emit('join', { channel_id });
      if (thread_channel_id) socket.emit('join', { channel_id: thread_channel_id });
    };

    if (socket.connected) join();
    socket.on('connect', join);
    return () => socket.off('connect', join);
  }, [socket, channel_id, thread_channel_id]);

  useEffect(() => {
    if (!socket) return undefined;

    const onMessageReceive = (item) => {
      if (item?.sender_id != userData?.id) {
        const reversedArr = [
          {
            id: item?.id,
            channel_id: item?.channel_id,
            reply_id: item?.reply_id,
            replied_message: item?.replied_message,
            sendMsgTime: item?.createdAt,
            message: item?.message,
            type: item?.type,
            media: item?.media,
            createdAt: item?.updatedAt,
            statusMsg: item?.status,
            sender_id: item?.sender_id,
            user: item?.sender,
            status: item?.status,
            chat_reactions: item?.chat_reactions,
            deleted_by_id: item?.deleted_by_id,
          },
        ];
        item?.channel_id == SelectedChatGroup?.id
          ? dispatch(MsgAction.SendMsg(reversedArr))
          : dispatch(MsgAction.SendThreads(reversedArr));
        item?.channel_id == SelectedChatGroup?.id
          ? setNewMessageRecieve((prev) => !prev)
          : setNewMessageRecieveThread((prev) => !prev);

        // Arriving into the open group or thread means it is already read.
        if (
          String(item?.channel_id) === String(channel_id) ||
          String(item?.channel_id) === String(thread_channel_id)
        ) {
          markChannelRead(item?.channel_id);
        }
      }
      // Whoever was typing has now sent, so drop the indicator.
      setTyping(null);
    };

    socket.on('message:receive', onMessageReceive);
    // Only this handler. The teardown used to also call `off('typing:alert')`,
    // which removed the typing listener registered by a different effect — and
    // since that effect only re-runs when the socket changes, the indicator
    // stayed dead for the rest of the session.
    return () => socket.off('message:receive', onMessageReceive);
  }, [socket, channel_id, thread_channel_id, markChannelRead, userData?.id]);

  // A channel admin withdrew a message from everyone. The reducer patches by
  // message_id, so it does not matter whether the bubble is currently on screen or
  // still paging in — and both streams are covered, since a retracted message can
  // be quoted in a thread.
  useEffect(() => {
    if (!socket) return undefined;

    const onMessageDeleted = (payload) => {
      dispatch(
        MsgAction.MessageDeletedForAll({
          message_id: payload?.message_id,
          deleted_by_id: payload?.deleted_by_id,
        })
      );
    };

    socket.on('message:deleted', onMessageDeleted);
    return () => socket.off('message:deleted', onMessageDeleted);
  }, [socket, dispatch]);

  /**
   * How many of this group's members are online.
   *
   * This was one `useEffect` keyed on `[socket]` alone. Two consequences: switching group
   * kept the PREVIOUS group's count (the effect never re-ran, because `channel_id` was not
   * a dependency, and `thread_channel_id` was read from a stale closure), and the number
   * never moved again for the rest of the session — it was a single snapshot taken when
   * the socket first appeared, with nothing subscribed to tell it people had come or gone.
   *
   * Now keyed on the channels it is actually counting, and re-run when a member's presence
   * changes. `presenceTick` is bumped by the `presence:changed` listener below rather than
   * the count being recomputed client-side, because the roster is paged — the client holds
   * at most the first 200 members and cannot count the rest.
   */
  const [presenceTick, setPresenceTick] = useState(0);

  /**
   * The header and the details panel now read the same two numbers.
   *
   * `Math.min` used to be applied at each call site to keep the online tally from
   * exceeding the membership — a clamp that existed because `members:status` counted
   * members who had LEFT (it had no `is_left` filter). That is fixed server-side, but the
   * clamp stays as a cheap invariant: the two numbers come from different reads, so a
   * count in flight during a join can still cross over for one render.
   */
  const groupOthersReadAt = othersReadAtByChannel?.[channel_id];
  const threadOthersReadAt = othersReadAtByChannel?.[thread_channel_id];

  const memberCount = Number(SelectedChatGroup?.member_count) || 0;
  const onlineCount = Math.min(Number(isMembers) || 0, memberCount);

  /**
   * The details panel's copy of the same pair. It reads `GroupDetails` — the channel record
   * from `GET /chat/group/:id` — which is the more authoritative total, falling back to the
   * list row when the panel has not been opened yet.
   */
  const detailsMemberCount = Number(GroupDetails?.member_count) || memberCount;
  const detailsOnlineCount = Math.min(Number(isMembers) || 0, detailsMemberCount);

  useEffect(() => {
    if (!isRealtimeUsable(socket) || !channel_id) return;
    socket.emit('members:status', { channel_id }, (data) => {
      if (data?.success !== false) setIsMembers(data?.count ?? 0);
    });
  }, [socket, channel_id, presenceTick]);

  useEffect(() => {
    if (!isRealtimeUsable(socket) || !thread_channel_id) {
      setIsMembersThread(0);
      return;
    }
    socket.emit('members:status', { channel_id: thread_channel_id }, (data) => {
      if (data?.success !== false) setIsMembersThread(data?.count ?? 0);
    });
  }, [socket, thread_channel_id, presenceTick]);

  useEffect(() => {
    if (!socket) return undefined;

    /**
     * Coalesced: a group of any size produces a burst of these when people arrive, and one
     * `members:status` round trip per event would be a request storm for a number that is
     * only ever read as a rough tally.
     */
    let pending;
    const onPresenceChanged = () => {
      clearTimeout(pending);
      pending = setTimeout(() => setPresenceTick((tick) => tick + 1), PRESENCE_RECOUNT_MS);
    };

    socket.on('presence:changed', onPresenceChanged);
    return () => {
      clearTimeout(pending);
      socket.off('presence:changed', onPresenceChanged);
    };
  }, [socket]);

  /**
   * Read receipts for the group stream.
   *
   * There was no listener here at all. The server fans `message:read:alert` out to every
   * member of a channel, group or not, so the receipts were being sent and thrown away —
   * and outgoing group messages had no delivery indicator of any kind.
   *
   * `others_read_at` is the server's read-by-EVERYONE aggregate (the minimum
   * `last_read_at` across the other live members), so a double tick here means what it
   * means in a 1:1 and the client never has to know the roster.
   */
  useEffect(() => {
    if (!socket) return undefined;

    const onReadAlert = (resp) => {
      const target = String(resp?.channel_id ?? '');
      if (target !== String(channel_id) && target !== String(thread_channel_id)) return;
      if (resp?.user_id === userData?.id) return;
      dispatch(
        MsgAction.ChannelReadReceipt({
          channel_id: resp.channel_id,
          others_read_at: resp?.others_read_at ?? null,
        })
      );
    };

    socket.on('message:read:alert', onReadAlert);
    return () => socket.off('message:read:alert', onReadAlert);
  }, [socket, channel_id, thread_channel_id, userData?.id, dispatch]);

  useEffect(() => {
    if (!socket) return undefined;

    let expiry;

    /**
     * A received indicator expires on its own.
     *
     * `status: false` was the only thing that cleared this, and it cannot be
     * relied on: the sender's tab can close, their connection can drop, they can
     * leave text in the composer and walk away — and an older client does not emit
     * it at all. Any of those left "…is typing" on screen indefinitely.
     *
     * Every fresh signal restarts the window, so continuous typing keeps it lit.
     */
    const onTyping = (resp) => {
      clearTimeout(expiry);
      setTyping(resp);
      if (resp?.status) expiry = setTimeout(() => setTyping(null), TYPING_EXPIRY_MS);
    };

    socket.on('typing:alert', onTyping);
    // This effect returned nothing, so the listener survived unmount and a second
    // copy was added on every remount.
    return () => {
      clearTimeout(expiry);
      socket.off('typing:alert', onTyping);
    };
  }, [socket]);

  // Hiding the button is presentation; this is the actual latch. The modal is also
  // reachable if `can_administer` flips to false while it is open (a demotion mid-session).
  const addMembersModal = () => {
    if (!canAdministerChannel) return;
    setAddMembersModalOpen(true);
  };

  const handleLeaveGroup = () => {
    setLeaveGroupModal(true);
  };

  const confirmLeaveGroup = () => {
    // The server refuses an owner's leave until ownership has been handed over,
    // so a confirmed "Yes" here could only ever produce an error toast.
    if (mustTransferBeforeLeaving) return;
    setLeaveGroupModal(false);
    dispatch(
      MsgAction.LeaveGroup(SelectedChatGroup?.id, () => {
        navigate('/messages');
      })
    );
  };

  /**
   * Take an owner from "I want to leave" to the only screen that lets them: the
   * roster, where Transfer ownership lives. Hiding the Leave button from an owner
   * would have been simpler and worse — it leaves them with no route and nothing
   * explaining why.
   */
  const startOwnershipHandover = () => {
    setLeaveGroupModal(false);
    setRightSideOpen1(false);
    setRightSideOpen2(true);
    setShowHideMembers(true);
  };

  /**
   * Commit the pending roster action.
   *
   * Re-checks the permission at the moment of the write, not just when the button was
   * rendered: `can_administer` and `my_role` are refreshed by every one of these sagas
   * (each re-reads the group on success), so a transfer performed in another tab can
   * demote this viewer while the confirmation is still open.
   */
  const runMemberAction = () => {
    const { kind, person } = memberAction ?? {};
    const channel_id = SelectedChatGroup?.id;
    const member_id = person?.member_id;
    setMemberAction(null);

    if (!kind || !channel_id || !member_id) return;
    if (!memberPermissions(person)[kind]) return;

    const payload = { channel_id, member_id };
    if (kind === 'promote') {
      dispatch(MsgAction.PromoteChannelAdmins({ channel_id, member_ids: [member_id] }));
    } else if (kind === 'demote') {
      dispatch(MsgAction.DemoteChannelAdmin(payload));
    } else if (kind === 'transfer') {
      dispatch(MsgAction.TransferChannelOwnership(payload));
    } else if (kind === 'remove') {
      dispatch(MsgAction.RemoveChannelMember(payload));
    }
  };

  /**
   * Open the details panel, replacing the thread panel if it is up. Used by the
   * header's avatar and name — clicking the subject of a conversation should
   * only ever reveal its details, never hide them.
   *
   * Swapping the panels is all this does now; useSharedMedia owns the fetch.
   */
  const seenMediaImages = () => {
    setRightSideOpen1(false);
    setRightSideOpen2(true);
  };

  /**
   * The header's details button, which toggles — matching the direct-message
   * pane. Closing also drops the thread panel, so the button's pressed state
   * cannot disagree with what is actually on screen.
   */
  const toggleGroupDetails = () => {
    if (rightSideOpen2) {
      setRightSideOpen2(false);
      return;
    }
    setRightSideOpen1(false);
    setRightSideOpen2(true);
  };

  /**
   * Cap the attachment queues.
   *
   * These used to assign `selectedImages.length = 5`, which truncates the array
   * in place without changing its identity: React saw no new value, never
   * re-rendered, and the effect keyed on that same array never re-ran. The user
   * was told about a limit that stayed invisible — every thumbnail remained on
   * screen while the array behind it held five. `slice` returns a new array, so
   * the tray and the payload agree. The message goes through the app's toast
   * rather than a blocking `alert`.
   */
  useEffect(() => {
    if (selectedImages?.length > MAX_ATTACHMENTS) {
      Utils.showMessage('warning', `You can attach up to ${MAX_ATTACHMENTS} files at a time.`);
      setSelectedImages((prev) => prev.slice(0, MAX_ATTACHMENTS));
    }
  }, [selectedImages]);

  useEffect(() => {
    if (selectedImagesThread?.length > MAX_ATTACHMENTS) {
      Utils.showMessage('warning', `You can attach up to ${MAX_ATTACHMENTS} files at a time.`);
      setSelectedImagesThread((prev) => prev.slice(0, MAX_ATTACHMENTS));
    }
  }, [selectedImagesThread]);

  useEffect(() => {
    dispatch(MsgAction.UnreadMessageDot());
    dispatch(MsgAction.EmptyCallHistory());
  }, [dispatch]);

  // The form's defaultValues are captured on first render, when the group is
  // often not selected yet — without this the edit modal opened blank and a
  // straight save wiped the name and description.
  useEffect(() => {
    if (!editGroup) return;
    resetEdit({
      groupName: SelectedChatGroup?.name || '',
      description: SelectedChatGroup?.description || '',
    });
    setSecretGroupValue(Boolean(SelectedChatGroup?.is_secret));
    setImageURL(SelectedChatGroup?.media?.media_url || null);
    setFile(null);
  }, [editGroup, SelectedChatGroup, resetEdit]);

  // Keyed on the group so switching groups without unmounting the pane still
  // refreshes the member list and the threads shown in the details panel.
  useEffect(() => {
    if (!SelectedChatGroup?.id) return;
    dispatch(MsgAction.GroupMembers(SelectedChatGroup?.id));
    dispatch(MsgAction.ThreadsListing({ group_id: SelectedChatGroup?.id }));
    setRightSideOpen1(false);
  }, [dispatch, SelectedChatGroup?.id, setRightSideOpen1]);

  //Functions
  /**
   * Start a group call. The created call's id goes straight into the URL — that is
   * what makes the call survive a reload, so it must not be dropped here.
   */
  const placeCall = (type) => {
    const Data = {
      receiver_ids: [],
      type, // AUDIO,VIDEO
      group_id: SelectedChatGroup?.id,
      method: 'GROUP', // SINGLE,MULTILPE,GROUP
    };
    dispatch(
      MsgAction.StartCall(Data, (call) => {
        navigate(call?.id ? ROUTES.CALL_ROOM(call.id) : ROUTES.CALLING);
      })
    );
  };

  const makeVoiceCall = () => placeCall('AUDIO');
  const makeVideoCall = () => placeCall('VIDEO');

  const uploadThreadMedia = async (formData) => {
    return new Promise((resolve, reject) => {
      dispatch(
        MsgAction.UploadThreadMedia(formData, (data) => {
          const res = data?.data?.chatMedia;

          if (res) {
            resolve(res); // Resolve the Promise with the chatMedia value
          } else {
            reject('Failed to get chatMedia'); // Reject the Promise if no chatMedia is found
          }
        })
      );
    });
  };

  const handlePlay = (e) => {
    const allAudios = document.querySelectorAll('audio');
    let Current = e.target.currentTime;
    // get all audio elements and stop them all
    allAudios.forEach((audioEl) => {
      if (!audioEl.paused && e.target.id == audioEl.id) {
        e.target.currentTime = Current;
        e.target.play();
      } else {
        audioEl.pause();
        audioEl.currentTime = 0;
      }
    });
  };

  const handleImageThreadClick = async () => {
    try {
      if (isRecordingThread) {
        // Stop recording
        mediaRecorderThread.stop();
        setIsRecordingThread(false);
        clearInterval(intervalThreadRef.current);
        return false;
      } else {
        // Discard previous recording
        setAudioUrlThread(null);
        audioChunksThread.current = [];

        // Start new recording

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        // Held so the unmount cleanup can release the microphone. Navigating away
        // mid-recording never stops the recorder, so `onstop` never fires and the
        // capture below would otherwise stay open for the life of the tab.
        threadStreamRef.current = stream;
        const newMediaRecorderThread = new MediaRecorder(stream);

        newMediaRecorderThread.ondataavailable = (event) => {
          audioChunksThread.current.push(event.data);
        };

        newMediaRecorderThread.start();
        setMediaRecorderThread(newMediaRecorderThread);
        setIsRecordingThread(true);
        setElapsedThreadTime(0); // Reset the time
        intervalThreadRef.current = setInterval(() => {
          setElapsedThreadTime((prevTime) => {
            const newTime = prevTime + 1;

            // Automatically stop the recording at 60 seconds
            if (newTime >= 60) {
              newMediaRecorderThread.stop();
              setIsRecordingThread(false);
              Utils.showMessage('error', 'Voice messages are limited to 1 minute');
              clearInterval(intervalThreadRef.current);
            }

            return newTime;
          });
        }, 1000);

        // Use a Promise to return the result after onstop
        return new Promise((resolve) => {
          newMediaRecorderThread.onstop = async () => {
            // MediaRecorder emits webm/ogg/mp4 (never mp3) — use the real mime
            // type + matching extension so the file plays back and S3 serves the
            // right content-type.
            const mimeType = newMediaRecorderThread.mimeType || 'audio/webm';
            const ext = mimeType.includes('ogg')
              ? 'ogg'
              : mimeType.includes('mp4')
                ? 'm4a'
                : 'webm';
            const audioBlob = new Blob(audioChunksThread.current, { type: mimeType });
            setAudioUrlThread(audioBlob);
            try {
              const formData = new FormData();
              formData.append('media', audioBlob, `recording.${ext}`);
              await uploadThreadMedia(formData);
            } catch {
              setAudioUrlThread(null);
              Utils.showMessage('error', 'Failed to upload the audio message. Please try again.');
            }
            audioChunksThread.current = [];
            // Release the microphone. `MediaRecorder.stop()` only stops RECORDING —
            // the stream is a separate object and its tracks stay `live`, so the
            // browser kept showing the recording indicator after the note was sent
            // and the device stayed claimed for the life of the tab.
            stream.getTracks().forEach((track) => track.stop());
            threadStreamRef.current = null;
            resolve(true); // Resolve the promise with true when recording stops
          };
        });
      }
    } catch {
      // If getUserMedia resolved and MediaRecorder construction is what threw, the
      // capture is already open — release it rather than leaving it to the unmount.
      threadStreamRef.current?.getTracks().forEach((track) => track.stop());
      threadStreamRef.current = null;
      setIsRecordingThread(false);
      Utils.showMessage(
        'error',
        'Could not access the microphone. Check your browser permissions.'
      );
    }
  };

  const ReportModalOpen = (msg) => {
    setModal(true);
    setReportMsgId(msg?.id);
  };

  const createThread = (msg) => {
    dispatch(MsgAction.ClearChatThreads());
    // const Data = {
    //   message_id:msg?.id || msg?.message_ID,
    //   group_id:msg?.channel_id,
    //   name: msg?.message
    //   }
    // startLoader();
    socket?.emit(
      'create:thread',
      {
        group_id: msg?.channel_id,
        message_id: msg?.id || msg?.message_ID,
        name: msg?.message,
      },
      async (response) => {
        if (response) {
          dispatch(MsgAction.ThreadsListing({ group_id: SelectedChatGroup?.id }));
          dispatch(
            MsgAction.ThreadsDetails(response?.threadData?.id, (newData) => {
              setThreadDataDelete(response?.threadData?.start_from_user_id);
              if (!newData?.isJoined) {
                dispatch(MsgAction.JoinThreads(newData?.id));
              }
            })
          );
          const Data = {
            offset: 0,
            id: response?.threadData?.id,
            fromApi: true,
          };
          dispatch(
            MsgAction.ThreadChatChannel(Data, () => {
              setRightSideOpen2(false);
              setRightSideOpen1(true);
            })
          );
        }
      }
    );
  };

  // A thread is a panel beside the group, not a destination — opening one must
  // not change the URL, or the group route would point at a thread id.
  const openThread = (threadId) => {
    if (!threadId) return;
    dispatch(MsgAction.ClearChatThreads());
    dispatch(
      MsgAction.ThreadsDetails(threadId, (data) => {
        if (!data?.isJoined) {
          dispatch(MsgAction.JoinThreads(threadId));
        }
        setRightSideOpen2(false);
        setRightSideOpen1(true);
      })
    );
    dispatch(MsgAction.ThreadChatChannel({ id: threadId, fromApi: true }));
  };

  const viewThread = (msg) => openThread(msg?.thread?.id);

  const renderThreadIndicator = (msg, isOut = false) => {
    if (!msg?.thread) return null;
    const detailedThread = GroupThreads?.find((t) => t.id === msg?.thread?.id) || msg?.thread;
    /**
     * Who replied — shown as a small avatar stack beside the count.
     *
     * Never in a secret group. The whole point of the mode is that a message
     * cannot be attributed to a person, and a face beside "7 replies" attributes
     * it precisely. The server redacts these too (`getThreads` in
     * chat.service.js); this is the second lock, because a client that renders
     * whatever identity it is handed is one endpoint away from leaking again.
     *
     * The count itself stays: it says how much conversation there is, not who had
     * it.
     */
    const repliers = SelectedChatGroup?.is_secret
      ? []
      : detailedThread?.members?.map((m) => m.user) ||
        detailedThread?.participants ||
        detailedThread?.users ||
        [];
    const replyCount =
      detailedThread?.reply_count ||
      detailedThread?.message_count ||
      (detailedThread?.chats?.length > 0 ? detailedThread?.chats?.length : null);

    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          viewThread(msg);
        }}
        className={`mt-2 flex items-center cursor-pointer ${isOut ? 'justify-end mr-2' : 'justify-start ml-2'}`}
      >
        {!isOut && repliers.length > 0 && <ReplierStack repliers={repliers} className="mr-2" />}
        <Text.Basic
          style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--accent)' }}
          className="hover:underline"
          text={replyCount ? `${replyCount} replies` : 'View thread'}
        />
        {isOut && repliers.length > 0 && <ReplierStack repliers={repliers} className="ml-2" />}
      </div>
    );
  };

  const EditModalClose = () => {
    setModal(false);
    reset();
    setReportMsgId(null);
  };
  const onSubmitFirstModal = (data) => {
    const Data = {
      description: data?.description,
      type: 'MESSAGE',
      id: reportMsgId,
    };
    dispatch(
      MsgAction.ReportMsg(Data, () => {
        setModal(false);
        setReportMsgId(null);
      })
    );
    reset();
  };

  const CloseItem = () => navigate(ROUTES.MESSAGES);

  const handleFileInputChangeThread = (event) => {
    const files = Array.from(event.target.files);
    if (files?.length > MAX_ATTACHMENTS) {
      // Keyed, so selecting a batch of files cannot stack one toast per file.
      Utils.showMessage('warning', `You can attach up to ${MAX_ATTACHMENTS} files at a time.`, {
        key: 'attachment-limit',
      });
      return;
    }
    if (selectedImagesThread?.length <= 5) {
      files?.forEach((files) => {
        if (files.type.startsWith('image/')) {
          setSelectedImagesThread((prevImages) => [...prevImages, files]);
        }
        if (files.type.startsWith('video/')) {
          const videoElement = document.createElement('video');
          videoElement.src = URL.createObjectURL(files);
          videoElement.onloadedmetadata = () => {
            const duration = videoElement.duration;
            if (duration > MAX_VIDEO_SECONDS) {
              // One toast for the whole selection, not one per rejected clip.
              Utils.showMessage(
                'warning',
                `Videos must be under ${MAX_VIDEO_SECONDS} seconds. That clip was not attached.`,
                { key: 'attachment-video-length' }
              );
            } else {
              // Proceed with sending the video
              setSelectedImagesThread((prevImages) => [...prevImages, files]);
            }
            // The blob URL is registered against the DOCUMENT, not against this
            // element — so letting `videoElement` fall out of scope released
            // nothing, and every clip probed for its duration pinned its whole
            // file in memory for the life of the tab. Safe to revoke here: the
            // metadata this element existed to read has already been parsed.
            URL.revokeObjectURL(videoElement.src);
          };
        }
      });
    }
    event.target.value = null;
  };

  const handlePaperclipClickThread = () => {
    document.getElementById('fileInputThread').click();
  };
  const handleRemoveImageThread = (index) => {
    setSelectedImagesThread((prevImages) => prevImages.filter((_, i) => i !== index));
  };
  const handleRemoveAudioThread = (_index) => {
    setAudioUrlThread(null);
  };

  /**
   * Older history for the group stream and for an open thread.
   *
   * Both replace hand-rolled `scrollTop == 0` handlers with the same faults as the
   * direct-message pane's: an exact equality that never held on a fractional-DPI
   * display, a stop condition read from a total rather than from what had been loaded,
   * an offset read from a stale closure, and no scroll anchoring. The thread one had an
   * additional fault of its own — `setOffsetThread` sat OUTSIDE the in-flight guard, so
   * the offset advanced even when the request was suppressed, skipping a page of
   * replies outright.
   */
  const fetchOlderGroup = useCallback(
    (cursor) => {
      // Only a cursor page is a prepend; the cursor-less first page replaces the stream
      // and is positioned by `useStreamBottomOnOpen`.
      if (cursor) captureGroupAnchor();
      dispatch(MsgAction.MessageChatChannel({ cursor, id: SelectedChatGroup?.id, fromApi: true }));
    },
    [dispatch, SelectedChatGroup?.id, captureGroupAnchor]
  );

  const { sentinelRef: groupSentinelRef, exhausted: groupHistoryExhausted } = useInfiniteList({
    resetKey: SelectedChatGroup?.id,
    fetchPage: fetchOlderGroup,
    loading: message_chats_loader,
    loadedCount: GroupChatMsgs?.length ?? 0,
    nextCursor: messageNextCursor,
    enabled: Boolean(SelectedChatGroup?.id),
    edge: 'start',
    // See MessagesChat: pages for every conversation land in this one slice, so a page
    // still in flight when the group changes has to be recognised as not this one's.
    loadedFor: message_chats_channel_id,
  });

  const fetchOlderThread = useCallback(
    (cursor) => {
      if (cursor) captureThreadAnchor();
      dispatch(MsgAction.ThreadChatChannel({ cursor, id: ThreadDetails?.id, fromApi: true }));
    },
    [dispatch, ThreadDetails?.id, captureThreadAnchor]
  );

  const { sentinelRef: threadSentinelRef, exhausted: threadHistoryExhausted } = useInfiniteList({
    resetKey: ThreadDetails?.id,
    fetchPage: fetchOlderThread,
    loading: threads_chats_loader,
    loadedCount: ThreadsMsgs?.length ?? 0,
    nextCursor: threadNextCursor,
    enabled: Boolean(ThreadDetails?.id),
    edge: 'start',
    loadedFor: threads_chats_channel_id,
  });

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleImageChange = (e) => {
    const fileUrl = e.target.files[0];
    if (fileUrl) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageURL(reader.result);
      };
      reader.readAsDataURL(fileUrl);
      setFile(fileUrl);
    }
  };
  const sendThread = () => {
    if (SelectedChatGroup?.is_secret) {
      const regexString =
        '^(?=.*(?:facebook.com|instagram.com|twitter.com|linkedin.com|youtube.com|tiktok.com|snapchat.com|telegram.me|t.me))|(?=.*([\\d][\\s-]?[\\d][\\s-]?[\\d][\\s-]?[\\d][\\s-]?[\\d][\\s-]?[\\d][\\s-]?[\\d][\\s-]?[\\d][\\s-]?[\\d])).*$';
      const regex = new RegExp(regexString);
      if (regex.test(threadMessageInput)) {
        Utils.showMessage(
          'error',
          "You're not allowed to share social links or 9 Digit UID within this secret group!"
        );
        return;
      }
    }
    if (selectedImagesThread.length || threadMessageInput?.length || audioUrlThread) {
      if (!isRealtimeUsable(socket)) {
        Utils.showMessage('error', SOCKET_DOWN_MESSAGE, { key: 'socket-offline' });
        return;
      }

      // Held so a rejected reply can hand the text back rather than eating it.
      const draft = threadMessageInput;

      try {
        clearTimeout(threadTypingStopTimer.current);
        emitTyping(thread_channel_id, false);

        socket.emit(
          'message:send',
          {
            sender_id: userData?.id,
            channel_id: thread_channel_id,
            reply_id: replyOnMsgThread?.id
              ? replyOnMsgThread?.id
              : replyOnMsgThread?.message_id
                ? replyOnMsgThread?.message_id
                : replyOnMsgThread?.message_ID
                  ? replyOnMsgThread?.message_ID
                  : null,
            message: selectedImagesThread.length > 0 ? null : threadMessageInput,
            type: audioUrlThread ? 'AUDIO' : selectedImagesThread.length > 0 ? 'MEDIA' : 'TEXT',
            media: ThreadImagesUrl ? ThreadImagesUrl : null,
            current_location: null,
            duration: audioUrlThread ? elapsedThreadTime : null,
            isSecret: SelectedChatGroup?.is_secret,
          },
          (item) => {
            const failure = Utils.ackError(item);
            if (failure) {
              Utils.showMessage('error', failure);
              setThreadMessageInput((current) => current || draft);
              return;
            }

            const reversedArr = [
              {
                channel_id: item?.response?.channel_id,
                id: item?.response?.id,
                media: item?.response?.media,
                reply_id: item?.response?.reply_id,
                replied_message: item?.response?.replied_message,
                message: item?.response?.message,
                type: item?.response?.type,
                createdAt: item?.response?.createdAt,
                statusMsg: item?.response?.status,
                sender: item?.response?.sender,
                sender_id: item?.response?.sender_id,
                chat_reactions: item?.response?.chat_reactions,
                deleted_by_id: item?.response?.deleted_by_id,
              },
            ];
            dispatch(MsgAction.SendThreads(reversedArr));
            setNewMessageRecieveThread((prev) => !prev);
          }
        );

        setThreadMessageInput('');
        setSelectedImagesThread([]);
        setReplyOnMsgThread(null);
        setAudioUrlThread(null);
        setUnreadMsg(0);
        // Clear the indicator, don't unsubscribe from it.
        setTyping(null);
      } catch {
        Utils.showMessage('error', 'Message could not be sent');
        setThreadMessageInput((current) => current || draft);
      }
    }
  };
  const replyMsg = (e) => {
    setReplyOnMsg(e);
  };
  const replyMsgThread = (e) => {
    setReplyOnMsgThread(e);
  };

  /**
   * Callbacks handed to GroupComposer.
   *
   * The composer owns the draft, the attachments and the voice note; these are the
   * three things it has to reach back into this component for. All three are
   * `useCallback`'d so the memo on GroupComposer actually holds — a fresh function
   * identity per render would re-render it on every stream update, which is the exact
   * cost the extraction exists to remove.
   */
  const clearReply = useCallback(() => setReplyOnMsg(null), []);

  const handleSent = useCallback(() => {
    // The unread divider is cleared by sending, and the stream scrolls to newest.
    setUnreadMsg(0);
    setNewMessageRecieve((previous) => !previous);
  }, []);

  const openScheduleModal = useCallback(() => setIsmodalopen(true), []);

  const handleThreadMessageInput = (e) => {
    setThreadMessageInput(e.target.value);

    emitTyping(thread_channel_id, true);
    // Its own timer: the thread and the group are separate channels, and sharing
    // one would let a keystroke in the thread cancel the group's pending stop.
    clearTimeout(threadTypingStopTimer.current);
    threadTypingStopTimer.current = setTimeout(
      () => emitTyping(thread_channel_id, false),
      TYPING_IDLE_MS
    );
  };

  const deleteMsg = (e, index) => {
    const messageId = e?.message_id || e?.id || e?.message_ID;
    dispatch(MsgAction.DeleteMessage(messageId));
    // `message_id` as well as the index: the reducer prefers the id, because an index
    // is not stable while older history is still prepending at the head of the stream.
    dispatch(
      MsgAction.DeleteMessageNow({
        index,
        message_id: e?.id ?? messageId,
        deletedBy: userData?.id,
      })
    );
  };

  /**
   * Withdraw a message from every member of the channel (admins only).
   *
   * Confirmed first because it is not reversible and it reaches everyone — and push
   * notifications already delivered cannot be recalled, which the prompt says out loud
   * so an admin is not surprised by it afterwards.
   *
   * The message is held in state rather than confirmed inline: this used to call
   * `window.confirm`, the browser's own dialog, which is unstyled, unthemed, blocks the
   * main thread, and is suppressed entirely by some mobile browsers — turning a
   * confirmation that never appeared into a retraction that silently never happened.
   */
  const retractMsg = (msg) => {
    const messageId = msg?.id || msg?.message_id || msg?.message_ID;
    if (!messageId) return;
    setRetractTarget({ id: messageId, message: msg?.message ?? null });
  };

  const confirmRetract = () => {
    const messageId = retractTarget?.id;
    setRetractTarget(null);
    if (!messageId) return;
    dispatch(MsgAction.RetractMessage({ message_id: messageId, deleted_by_id: userData?.id }));
  };

  /**
   * Star / unstar a message.
   *
   * `is_saved` is sent as the DESIRED state so the saga never has to re-read the
   * stream to work out which way the toggle went. Available to every member — a
   * read-only subscriber of a broadcast channel can still keep a bookmark.
   */
  const toggleSaveMsg = (msg) => {
    const messageId = msg?.id || msg?.message_id || msg?.message_ID;
    if (!messageId) return;
    dispatch(MsgAction.ToggleSaveMessage({ message_id: messageId, is_saved: !msg?.is_saved }));
  };

  const deleteThread = (e) => {
    setDeleteThreadModal(false);
    dispatch(
      MsgAction.DeleteThread(e?.id, () => {
        dispatch(MsgAction.ThreadsListing({ group_id: SelectedChatGroup?.id }));
      })
    );
    setRightSideOpen1(false);
  };

  const deleteThreadMsg = (e, index) => {
    if (!e?.id) return;
    dispatch(MsgAction.DeleteMessage(e.id));
    dispatch(
      MsgAction.DeleteThreadMessageNow({
        indexThread: index,
        message_id: e.id,
        deletedByThread: userData?.id,
      })
    );
  };

  const onSubmitAddMembersModal = () => {
    if (!canAdministerChannel) {
      setAddMembersModalOpen(false);
      return;
    }
    const ids = selectedPeople?.map((person) => person.id);
    if (!ids?.length) return;
    const addPeopleInGroup = {
      group_id: SelectedChatGroup?.id,
      user_ids: ids,
      is_secret: SelectedChatGroup?.is_secret,
    };
    dispatch(MsgAction.AddUsersGroup(addPeopleInGroup));
    dispatch(MsgAction.GroupMembers(SelectedChatGroup?.id));
    setSelectedPeople([]);
    setInputValue('');
    setIsChecked(false);
    setAddMembersModalOpen(false);
  };
  const deleteImgae = () => {
    setImageURL(null);
    setFile(null);
  };
  const onSubmitEditModal = (data) => {
    const name = data?.groupName?.trim();
    const description = data?.description?.trim();
    const formData = new FormData();
    formData.append('name', name);
    formData.append('is_secret', secretGroupValue);
    formData.append('description', description);
    formData.append('name_edited', name !== SelectedChatGroup?.name);
    // Appending a null file would post the literal string "null"; the field has
    // to be omitted when the picture is untouched.
    if (file) formData.append('imageFile', file);
    if (SelectedChatGroup?.url) formData.append('url', SelectedChatGroup.url);

    const Data = {
      formData,
      id: SelectedChatGroup?.id,
    };
    dispatch(
      MsgAction.UpdateCreateGroup(Data, (response) => {
        const updated = response?.data;
        // Prefer the row the server echoes back: its media_url is the real S3
        // URL, where a local object URL would break on the next reload.
        dispatch(
          MsgAction.SelectedChatUser({
            ...SelectedChatGroup,
            ...updated,
            name: updated?.name ?? name,
            description: updated?.description ?? description,
            media: updated?.media ?? SelectedChatGroup?.media,
          })
        );
        dispatch(MsgAction.GroupMembers(SelectedChatGroup?.id));
        dispatch(MsgAction.GlobalSearchChatsGroups());
        setEditGroup(false);
        setFile(null);
        resetEdit();
      })
    );
  };

  const onSubmitMessage = (data) => {
    const scheduledDateTime = new Date(`${data.selected_date}T${data.selected_time}`);

    if (data.selected_message) {
      if (!isRealtimeUsable(socket)) {
        Utils.showMessage('error', SOCKET_DOWN_MESSAGE, { key: 'socket-offline' });
        return;
      }

      try {
        socket.emit(
          'message:send',
          {
            sender_id: userData?.id,
            channel_id: channel_id,
            message: data.selected_message,
            type: 'TEXT',
            media: null,
            current_location: null,
            duration: null,
            scheduled_at: scheduledDateTime,
            groupName: SelectedChatGroup?.name,
            isSecret: SelectedChatGroup?.is_secret,
          },
          (item) => {
            // Reported success unconditionally before, including for a refusal.
            const failure = Utils.ackError(item);
            if (failure) {
              Utils.showMessage('error', failure);
              return;
            }

            Utils.showMessage('success', 'Message scheduled successfully');
          }
        );
        resetMessage();
        setIsmodalopen(false);
      } catch {
        Utils.showMessage('error', 'Message could not be scheduled');
      }
    }
  };

  const handlePlaySound = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.currentTime = 0; // Reset playback position
      ringtoneRef.current.play().catch(() => {});
    }
  };

  // writeText is async and rejects on a denied clipboard permission. Wrapped in
  // try/catch it resolved after the success toast had already been shown, so a
  // failed copy still reported success.
  const copyToClipboard = (shareLinked) => {
    if (!shareLinked) return;
    Promise.resolve(navigator.clipboard?.writeText(shareLinked))
      .then(() => Utils.showMessage('success', 'Copied to clipboard'))
      .catch(() => Utils.showMessage('error', 'Could not copy the link'));
  };

  const handleThreadEmojiInsert = (emoji) =>
    setThreadMessageInput((previous) => `${previous ?? ''}${emoji}`);

  const canSendThread = Boolean(
    threadMessageInput?.trim() || audioUrlThread || selectedImagesThread.length
  );

  const threadSendAction = (
    <button
      type="button"
      title="Send"
      className={`composer-send-btn ${uploadThreadLoader || isRecordingThread ? 'cursor-not-allowed' : ''}`}
      disabled={uploadThreadLoader || isRecordingThread || !canSendThread}
      onClick={uploadThreadLoader || !canSendThread ? undefined : sendThread}
    >
      <IoSend size={18} />
    </button>
  );

  const threadSuffix = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {isRecordingThread ? (
        <span
          title="Stop recording"
          className={`composer-icon-btn ${selectedImagesThread.length > 0 || threadMessageInput?.length > 0 ? 'cursor-not-allowed' : ''}`}
          style={{ color: 'var(--accent)', animation: 'recPulse 1s infinite' }}
          onClick={
            selectedImagesThread.length > 0 || threadMessageInput?.length > 0
              ? ''
              : handleImageThreadClick
          }
        >
          <FaRecordVinyl size={18} />
        </span>
      ) : (
        <span
          title="Voice note"
          className={`composer-icon-btn ${selectedImagesThread.length > 0 || threadMessageInput?.length > 0 ? 'cursor-not-allowed' : ''}`}
          onClick={
            selectedImagesThread.length > 0 || threadMessageInput?.length > 0
              ? ''
              : handleImageThreadClick
          }
        >
          <FaMicrophone size={18} />
        </span>
      )}
      <span
        title="Attach"
        className={`composer-icon-btn ${isRecordingThread || audioUrlThread || threadMessageInput?.length > 0 ? 'cursor-not-allowed' : ''}`}
        style={{
          pointerEvents: selectedImagesThread.length >= 5 && 'none',
          opacity: selectedImagesThread.length >= 5 && '0.5',
        }}
        onClick={
          isRecordingThread || audioUrlThread || threadMessageInput?.length > 0
            ? ''
            : handlePaperclipClickThread
        }
      >
        <IoAttach size={20} />
      </span>
    </span>
  );

  return (
    <>
      <audio ref={ringtoneRef} src={emojiSound} />
      {create_thread_loader ? (
        <div
          className="flex justify-center items-center w-full"
          style={{
            backgroundColor: Colors.White,
          }}
        >
          <Loader.Circular color={Colors.MainColor} size={'large'} />
        </div>
      ) : (
        <>
          <main className="conversation-pane">
            <header className="pane-header">
              <PaneBack onBack={CloseItem} label="Back to conversations" />

              <span className="pane-avatar cursor-pointer" onClick={seenMediaImages}>
                {SelectedChatGroup?.media?.media_url ? (
                  <Image
                    className="object-cover"
                    height={38}
                    width={38}
                    style={{ borderRadius: 'var(--r-avatar)' }}
                    src={SelectedChatGroup?.media?.media_url}
                    preview={false}
                  />
                ) : (
                  <Avatar
                    shape="square"
                    size={38}
                    style={{ borderRadius: 'var(--r-avatar)' }}
                    icon={<UserOutlined />}
                  />
                )}
              </span>

              <div className="pane-title cursor-pointer" onClick={seenMediaImages}>
                {SelectedChatGroup?.name && (
                  <span
                    className="pane-title-name"
                    title={SelectedChatGroup?.name?.replace(/\b\w/g, (char) => char.toUpperCase())}
                  >
                    {SelectedChatGroup?.name?.replace(/\b\w/g, (char) => char.toUpperCase())}
                  </span>
                )}
                {/* `> 0`, not a bare truthiness test: `0 && <jsx>` evaluates to 0, which
                    React renders as the character "0" — a group whose count came back
                    zero printed a stray digit under its name. */}
                {memberCount > 0 && (
                  <span className="pane-title-status">
                    {/*
                      Reflects the tally beside it. This was hardcoded `is-online`, so the
                      header showed a green dot next to the words "0 online" — the details
                      panel had it right and the two contradicted each other. A channel has
                      no tally, so it gets no dot at all rather than a meaningless one.
                    */}
                    {!isChannel && (
                      <span
                        className={`presence-dot ${onlineCount > 0 ? 'is-online' : 'is-offline'}`}
                      />
                    )}
                    {/*
                      "members" in both, matching the details panel — a channel's people
                      are members like any group's. Channels get a read-only hint instead
                      of the online tally: their roster is admins-only for a subscriber,
                      so an online count derived from it would be wrong as well as
                      uninformative.
                    */}
                    <span className="pane-title-status-text">
                      {isChannel
                        ? `${memberCount} members${canPost ? '' : ' · read only'}`
                        : `${memberCount} members · ${onlineCount} online`}
                    </span>
                  </span>
                )}
              </div>

              {/* Tells you at a glance that posting here is restricted, and — for an
                  admin — that you are one of the people who can. */}
              {isChannel && (
                <span
                  title={
                    canPost
                      ? 'Broadcast channel — you can post here'
                      : 'Broadcast channel — only admins can post'
                  }
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    flex: '0 0 auto',
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    padding: '2px 7px',
                    borderRadius: 4,
                    color: 'var(--accent, #333c91)',
                    background: 'var(--accent-soft, #e4e7f8)',
                  }}
                >
                  <MdCampaign size={13} aria-hidden="true" />
                  {myChannelRole === MEMBER_ROLE.OWNER
                    ? 'Channel · Owner'
                    : myChannelRole === MEMBER_ROLE.ADMIN
                      ? 'Channel · Admin'
                      : 'Channel'}
                </span>
              )}

              <div style={{ flex: 1 }} />

              <div className="pane-actions">
                {/* No group calls in a broadcast channel — the server refuses them, so
                    rendering the buttons would only offer a control that always fails. */}
                {userData?.level != 'LEVEL1' && !isChannel && (
                  <>
                    {/* Same buttons, different meaning when a call is already up: the
                        server returns the live call rather than a new channel, so this
                        walks into the existing room. The title is the only thing that has
                        to say so. */}
                    <button
                      type="button"
                      title={activeGroupCall ? 'Join the call' : 'Voice call'}
                      className={`chat-action-btn${activeGroupCall ? ' is-accent' : ''}`}
                      onClick={() => makeVoiceCall()}
                    >
                      <IoCallOutline size={17} />
                    </button>
                    <button
                      type="button"
                      title={activeGroupCall ? 'Join with video' : 'Video call'}
                      className={`chat-action-btn${activeGroupCall ? ' is-accent' : ''}`}
                      onClick={() => makeVideoCall()}
                    >
                      <CiVideoOn size={18} />
                    </button>
                  </>
                )}
                {/* Toggles, like the direct-message pane's — it used to only
                    ever open, so the only way back out was the panel's own
                    close button. `is-accent` marks the panel as open. */}
                <button
                  type="button"
                  title="Group details"
                  aria-pressed={Boolean(rightSideOpen2)}
                  className={`chat-action-btn ${rightSideOpen2 ? 'is-accent' : ''}`}
                  onClick={toggleGroupDetails}
                >
                  <MdInfoOutline size={18} />
                </button>
              </div>
            </header>

            {/* A call in progress has to be visible, not just joinable.
                The ring is a one-shot: decline it, miss the sixty-second window, or be
                offline when it fires and there was previously nothing anywhere to say the
                group was on a call. The buttons above join it, but only if you know to
                press them. */}
            {activeGroupCall && userData?.level != 'LEVEL1' && !isChannel && (
              <div className="group-call-banner" role="status">
                <span className="group-call-banner-dot" aria-hidden="true" />
                <span className="group-call-banner-text">
                  {activeGroupCall.type === 'VIDEO' ? 'Video call' : 'Voice call'} in progress
                </span>
                <button type="button" className="group-call-banner-join" onClick={makeVoiceCall}>
                  Join
                </button>
              </div>
            )}

            <div ref={ScrollingRef} className="pane-stream" data-infinite-root>
              {/* This list pages BACKWARDS into older history, so the sentinel the
                  observer watches sits at the head of the stream, not the foot. */}
              {!groupHistoryExhausted && <div ref={groupSentinelRef} aria-hidden="true" />}
              {/* See MessagesChat: this pane had no loading state either, and read
                  the same non-existent `loading` key. */}
              {message_chats_loader && (
                <div className={`stream-loading ${GroupChatMsgs?.length ? 'is-more' : ''}`}>
                  <Loader.Circular size={GroupChatMsgs?.length ? 24 : 40} />
                </div>
              )}
              {GroupChatMsgs?.map((msg, index) => {
                const highlightIndex = GroupChatMsgs?.length - unreadMsg;
                // Oldest-first, so the message before this one is the day to
                // compare against. Null unless this message opens a new day.
                const dayLabel = dayDividerFor(msg?.createdAt, GroupChatMsgs[index - 1]?.createdAt);
                return (
                  // Keyed: see MessagesChat — a bare `<>` gave React no identity
                  // for the several siblings each message contributes.
                  <React.Fragment key={msg?.id ?? `idx-${index}`}>
                    <DayDivider label={dayLabel} />

                    {unreadMsg > 0 && index === highlightIndex && (
                      <div className="flex justify-between items-center mb-6">
                        <span
                          className="w-full h-0"
                          style={{ border: '1px solid var(--text)' }}
                        ></span>
                        <Text.Basic
                          className="mx-4 text-[#2B3674] whitespace-nowrap"
                          style={{
                            fontWeight: '700',
                            fontSize: '0.8rem',
                          }}
                          text={`${unreadMsg} Unread Messages`}
                        />
                        <span
                          className="w-full h-0"
                          style={{ border: '1px solid var(--text)' }}
                        ></span>
                      </div>
                    )}

                    {msg.sender_id != userData?.id && !msg?.deleted_by_id ? (
                      <div className="msg-row flex justify-start mb-6">
                        {/* Avatar, sender and time are the message card's own
                            header now — they used to be an avatar column plus a
                            header row OUTSIDE the bubble, so one message read as
                            three separate pieces. */}
                        <div className="relative msg-bubble message-width">
                          <MessageMenuTooltip
                            placement="rightBottom"
                            menu={
                              msg?.deleted_by_id != userData?.id ? (
                                <MessageMenu
                                  onReply={canReply ? () => replyMsg(msg) : null}
                                  onDelete={
                                    msg?.sender_id != userData?.id
                                      ? () => deleteMsg(msg, index)
                                      : null
                                  }
                                  onRetract={
                                    canAdministerChannel && isChannel && !msg?.deleted_by_id
                                      ? () => retractMsg(msg)
                                      : null
                                  }
                                  isSaved={Boolean(msg?.is_saved)}
                                  onToggleSave={
                                    msg?.deleted_by_id ? null : () => toggleSaveMsg(msg)
                                  }
                                  onReport={
                                    msg?.sender_id != userData?.id
                                      ? () => ReportModalOpen(msg)
                                      : null
                                  }
                                  onViewThread={
                                    msg?.type == 'TEXT' && msg?.thread
                                      ? () => viewThread(msg)
                                      : null
                                  }
                                  onCreateThread={
                                    canReply && msg?.type == 'TEXT' && !msg?.thread
                                      ? () => createThread(msg)
                                      : null
                                  }
                                />
                              ) : (
                                ''
                              )
                            }
                          >
                            <div
                              className={`${msg?.reply_id ? 'border-4 border-[#f5f8ff]' : ''} rounded-xl bg-[#f5f8ff] px-4 py-2`}
                            >
                              <div className="msg-head">
                                {SelectedChatGroup?.is_secret ? (
                                  <PlaceholderAvatar
                                    size={28}
                                    variant="anonymous"
                                    seed={`${msg?.user?.first_name}${msg?.user?.id}`}
                                  />
                                ) : senderAvatar(msg) ? (
                                  <img
                                    className="msg-head-avatar"
                                    src={senderAvatar(msg)}
                                    alt=""
                                    width={28}
                                    height={28}
                                    loading="lazy"
                                    decoding="async"
                                  />
                                ) : (
                                  <PlaceholderAvatar size={28} />
                                )}
                                <span className="msg-head-name">
                                  {SelectedChatGroup?.is_secret
                                    ? msg?.user?.member_mode
                                    : senderName(msg)}
                                </span>
                                <span className="msg-head-time">
                                  {new Date(msg?.createdAt).toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true,
                                  })}
                                </span>
                              </div>
                              {msg?.reply_id ? (
                                <div
                                  style={{
                                    backgroundColor: Colors.White,
                                  }}
                                  className="w-full p-2 border border-[#f5f8ff] rounded-lg"
                                >
                                  <Text.Basic
                                    style={{
                                      fontWeight: '800',
                                      fontSize: '0.9rem',
                                    }}
                                    className="text-[#2B3674]"
                                    text={
                                      SelectedChatGroup?.is_secret
                                        ? msg?.replied_message?.user?.member_mode
                                        : msg?.replied_message?.user?.first_name?.replace(
                                            /\b\w/g,
                                            (char) => char.toUpperCase()
                                          )
                                    }
                                  />
                                  <Col span={24}>
                                    {msg?.replied_message?.type === 'TEXT' ? (
                                      <Text.Basic
                                        style={{
                                          fontWeight: '400',
                                          fontSize: '0.8rem',
                                        }}
                                        className="text-[#2B3674]"
                                        text={msg?.replied_message?.message}
                                      />
                                    ) : msg?.replied_message?.duration ? (
                                      <span>
                                        <Avatar size={30} shape="square" icon={<GiSoundWaves />} />
                                        <Text.Basic
                                          style={{
                                            fontWeight: '400',
                                            fontSize: '0.8rem',
                                          }}
                                          className="text-[#2B3674] ml-2"
                                          text={'Audio'}
                                        />
                                      </span>
                                    ) : (
                                      <span>
                                        <Avatar size={20} shape="square" icon={<UserOutlined />} />
                                        <Text.Basic
                                          style={{
                                            fontWeight: '400',
                                            fontSize: '0.8rem',
                                          }}
                                          className="text-[#2B3674] ml-2"
                                          text={'MEDIA'}
                                        />
                                      </span>
                                    )}
                                  </Col>
                                </div>
                              ) : (
                                ''
                              )}
                              {msg?.type === 'TEXT' ? (
                                msg?.deleted_by_id != userData?.id ? (
                                  <div className={`${msg?.reply_id ? 'bg-[#f5f8ff]' : ''}`}>
                                    <MessageText
                                      style={{
                                        fontWeight: '400',
                                        fontSize: '0.8rem',
                                      }}
                                      className="text-[#2B3674]"
                                      text={msg?.message}
                                      matchMentions={matchMentions}
                                      selfName={userData?.first_name}
                                    />
                                  </div>
                                ) : (
                                  <Text.Basic
                                    style={{
                                      fontWeight: '400',
                                      fontSize: '0.6rem',
                                    }}
                                    className="text-[#2B3674]"
                                    text={'This message has been deleted'}
                                  />
                                )
                              ) : msg?.deleted_by_id != userData?.id ? (
                                msg?.media?.map((msg, index) =>
                                  msg?.format?.startsWith('audio/') ? (
                                    <ReactAudioPlayer
                                      src={msg?.media_url}
                                      autoPlay={false}
                                      controls
                                      onPlay={(e) => handlePlay(e, index)}
                                      id={index}
                                    />
                                  ) : msg?.format?.startsWith('video/') ? (
                                    <ReactPlayer
                                      height={200}
                                      width={200}
                                      url={msg?.media_url}
                                      controls={true}
                                    />
                                  ) : msg?.format?.startsWith('image/') ? (
                                    <Image
                                      className="rounded-xl object-cover"
                                      // Thumbnail in the bubble, original only when it is
                                      // actually opened. This rendered `media_url` — the
                                      // full-resolution upload — into a 200px box, so a
                                      // conversation of phone photos pulled several MB per
                                      // message to display a thumbnail's worth of pixels.
                                      // `media_thumb_url` is what the shared-media grid
                                      // has always used.
                                      src={msg?.media_thumb_url || msg?.media_url}
                                      preview={{ src: msg?.media_url }}
                                      width={200}
                                      height={200}
                                      loading="lazy"
                                      decoding="async"
                                    />
                                  ) : (
                                    ''
                                  )
                                )
                              ) : (
                                <Text.Basic
                                  style={{
                                    fontWeight: '400',
                                    fontSize: '0.6rem',
                                  }}
                                  className="text-[#2B3674]"
                                  text={'This message has been deleted'}
                                />
                              )}{' '}
                            </div>
                          </MessageMenuTooltip>
                          {/*
                            Reactions belong to the bubble, so they sit against it;
                            the thread summary is a separate affordance and goes
                            below them. Rendered the other way round, a message that
                            had both pushed its pills a row past the thread avatars,
                            where they read as belonging to the thread rather than to
                            the message.
                          */}
                          <MessageReactions
                            reactions={msg?.chat_reactions}
                            currentUserId={userData?.id}
                            onOpenList={reactedListingModal}
                          />
                          {renderThreadIndicator(msg, false)}
                        </div>
                        <ReactionPicker
                          emojis={EmojiLists}
                          reactions={msg?.chat_reactions}
                          currentUserId={userData?.id}
                          isOpen={currentIndex === index}
                          onToggle={() => setCurrentIndex(currentIndex === index ? -1 : index)}
                          onClose={() => setCurrentIndex(-1)}
                          onSelect={(emoji) => applyReaction(msg, emoji)}
                          align="right"
                          className="ml-2 shrink-0"
                        />
                      </div>
                    ) : (
                      <>
                        {!msg?.deleted_by_id && (
                          <div className="msg-row flex justify-end mb-6 relative">
                            <ReactionPicker
                              emojis={EmojiLists}
                              reactions={msg?.chat_reactions}
                              currentUserId={userData?.id}
                              isOpen={currentIndex === index}
                              onToggle={() => setCurrentIndex(currentIndex === index ? -1 : index)}
                              onClose={() => setCurrentIndex(-1)}
                              onSelect={(emoji) => applyReaction(msg, emoji)}
                              align="left"
                              className="ml-2 mr-2"
                            />
                            {/* `is-out` carries no styling — it tells the motion
                                layer to enter from the sender's side. */}
                            <div className="relative msg-bubble is-out message-width flex flex-col items-end mr-2">
                              <MessageMenuTooltip
                                placement="leftBottom"
                                menu={
                                  msg?.deleted_by_id != userData?.id ? (
                                    <MessageMenu
                                      onReply={canReply ? () => replyMsg(msg) : null}
                                      onDelete={
                                        msg?.sender_id == userData?.id
                                          ? () => deleteMsg(msg, index)
                                          : null
                                      }
                                      onRetract={
                                        canAdministerChannel && isChannel && !msg?.deleted_by_id
                                          ? () => retractMsg(msg)
                                          : null
                                      }
                                      isSaved={Boolean(msg?.is_saved)}
                                      onToggleSave={
                                        msg?.deleted_by_id ? null : () => toggleSaveMsg(msg)
                                      }
                                      onViewThread={
                                        msg?.type == 'TEXT' && msg?.thread
                                          ? () => viewThread(msg)
                                          : null
                                      }
                                      onCreateThread={
                                        canReply && msg?.type == 'TEXT' && !msg?.thread
                                          ? () => createThread(msg)
                                          : null
                                      }
                                    />
                                  ) : (
                                    ''
                                  )
                                }
                              >
                                <div
                                  className={`text-end rounded-xl px-4 py-1 h-fit w-full`}
                                  style={{
                                    backgroundColor: Colors.MainColor,
                                  }}
                                >
                                  <div className="flex justify-start">
                                    <div
                                      className={`${msg?.reply_id && msg?.type == 'TEXT' ? 'border-4' : msg?.reply_id && msg?.type == 'MEDIA' ? 'border-8 rounded-xl' : ''} ${msg?.reply_id ? `border-[#f56469]` : ''} w-full `}
                                    >
                                      {msg?.reply_id ? (
                                        <div
                                          style={{
                                            backgroundColor: Colors.White,
                                          }}
                                          className={`p-2 rounded-lg`}
                                        >
                                          <Text.Basic
                                            style={{
                                              fontWeight: '800',
                                              fontSize: '0.9rem',
                                            }}
                                            className="text-[#2B3674]"
                                            text={
                                              SelectedChatGroup?.is_secret
                                                ? msg?.replied_message?.user?.member_mode
                                                : msg?.replied_message?.user?.first_name?.replace(
                                                    /\b\w/g,
                                                    (char) => char.toUpperCase()
                                                  )
                                            }
                                          />
                                          <Col span={24}>
                                            {msg?.replied_message?.type === 'TEXT' ? (
                                              <Text.Basic
                                                style={{
                                                  fontWeight: '400',
                                                  fontSize: '0.8rem',
                                                }}
                                                className="text-[#2B3674]"
                                                text={msg?.replied_message?.message}
                                              />
                                            ) : msg?.replied_message?.duration ? (
                                              <span>
                                                <Avatar
                                                  size={30}
                                                  shape="square"
                                                  icon={<GiSoundWaves />}
                                                />
                                                <Text.Basic
                                                  style={{
                                                    fontWeight: '400',
                                                    fontSize: '0.8rem',
                                                  }}
                                                  className="text-[#2B3674] ml-2"
                                                  text={'Audio'}
                                                />
                                              </span>
                                            ) : (
                                              <span>
                                                <Avatar
                                                  size={20}
                                                  shape="square"
                                                  icon={<UserOutlined />}
                                                />
                                                <Text.Basic
                                                  style={{
                                                    fontWeight: '400',
                                                    fontSize: '0.8rem',
                                                  }}
                                                  className="text-[#2B3674] ml-2"
                                                  text={'MEDIA'}
                                                />
                                              </span>
                                            )}
                                          </Col>
                                        </div>
                                      ) : (
                                        ''
                                      )}
                                      {msg?.type === 'TEXT' ? (
                                        msg?.deleted_by_id != userData?.id ? (
                                          <div
                                            className={`${msg?.reply_id ? `bg-${Colors.MainColor}` : ''} flex`}
                                          >
                                            <MessageText
                                              style={{
                                                fontWeight: '400',
                                                fontSize: '0.8rem',
                                              }}
                                              className={`text-white`}
                                              text={msg?.message}
                                              matchMentions={matchMentions}
                                              selfName={userData?.first_name}
                                              onAccent
                                            />
                                          </div>
                                        ) : (
                                          <Text.Basic
                                            style={{
                                              fontWeight: '400',
                                              fontSize: '0.6rem',
                                            }}
                                            className="text-[#2B3674]"
                                            text={'This message has been deleted'}
                                          />
                                        )
                                      ) : msg?.deleted_by_id != userData?.id ? (
                                        msg?.media?.map((msg, index) =>
                                          msg?.format?.startsWith('audio/') ? (
                                            <ReactAudioPlayer
                                              src={msg?.media_url}
                                              autoPlay={false}
                                              controls
                                              onPlay={(e) => handlePlay(e, index)}
                                              id={index}
                                            />
                                          ) : msg?.format?.startsWith('video/') ? (
                                            <ReactPlayer
                                              height={200}
                                              width={200}
                                              url={msg?.media_url}
                                              controls={true}
                                            />
                                          ) : msg?.format?.startsWith('image/') ? (
                                            <Image
                                              className="object-cover"
                                              // See the group stream above — thumbnail in
                                              // the bubble, original on open.
                                              src={msg?.media_thumb_url || msg?.media_url}
                                              preview={{ src: msg?.media_url }}
                                              width={200}
                                              height={200}
                                              loading="lazy"
                                              decoding="async"
                                            />
                                          ) : (
                                            ''
                                          )
                                        )
                                      ) : (
                                        <Text.Basic
                                          style={{
                                            fontWeight: '400',
                                            fontSize: '0.6rem',
                                          }}
                                          className="text-[#2B3674]"
                                          text={'This message has been deleted'}
                                        />
                                      )}
                                    </div>
                                  </div>
                                  <div className="msg-meta is-on-accent">
                                    {new Date(msg?.createdAt).toLocaleTimeString([], {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: true,
                                    })}
                                    {/* Group messages had no delivery indicator at all,
                                        while the server was already fanning
                                        `message:read:alert` out to every member of the
                                        channel — the receipts were being sent and dropped
                                        on the floor.

                                        A double tick here means read by EVERYONE else:
                                        `others_read_at` is the minimum `last_read_at`
                                        across the other live members, so one straggler
                                        keeps it at a single tick. Same field, same
                                        comparison as the 1:1 pane. */}
                                    <MessageTicks
                                      state={
                                        msg?.failed
                                          ? 'failed'
                                          : msg?.pending
                                            ? 'pending'
                                            : groupOthersReadAt &&
                                                new Date(groupOthersReadAt) >=
                                                  new Date(msg?.createdAt)
                                              ? 'read'
                                              : 'sent'
                                      }
                                    />
                                  </div>
                                </div>
                              </MessageMenuTooltip>
                              {/* Reactions against the bubble, thread summary below —
                                  same order as the incoming branch. */}
                              <MessageReactions
                                reactions={msg?.chat_reactions}
                                currentUserId={userData?.id}
                                isOut
                                onOpenList={reactedListingModal}
                              />
                              {renderThreadIndicator(msg, true)}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Tail of the stream, so it sits under the newest message and
                  `lastMessageRef` scrolls it into view like any other arrival.
                  Named here: in a group, WHO is typing is the point. */}
              {isPeerTyping && <TypingIndicator name={typing?.name} />}

              <div ref={lastMessageRef} />
            </div>

            {/*
              In a broadcast channel a non-admin gets a read-only bar instead of the
              composer. This is presentation only — the server refuses the write on
              every path regardless, so a client that renders the composer anyway
              simply gets a BROADCAST_READ_ONLY ack.
            */}
            {!canPost ? (
              <div className="pane-composer">
                <Col span={24}>
                  <div
                    className="flex items-center justify-center gap-2 py-3"
                    style={{ color: 'var(--muted)', fontSize: 13.5 }}
                    role="status"
                  >
                    <MdCampaign size={18} aria-hidden="true" />
                    <span>Only admins can post in this channel</span>
                  </div>
                </Col>
              </div>
            ) : (
              <div className="pane-composer">
                <Col span={24}>
                  {/* Draft text, attachments, voice note and the @mention picker all
                      live in GroupComposer now. They used to be state on THIS component,
                      the one that renders the message stream — so every keystroke, and
                      every tick of the recording timer, re-rendered every loaded message
                      bubble. See the note at the top of GroupComposer. */}
                  <GroupComposer
                    channelId={channel_id}
                    isSecret={isSecretGroup}
                    canPost={canPost}
                    userData={userData}
                    socket={socket}
                    mentionRoster={convertedGroupUsers}
                    mentionsEnabled={mentionsEnabled}
                    replyOnMsg={replyOnMsg}
                    onClearReply={clearReply}
                    emitTyping={emitTyping}
                    onSent={handleSent}
                    onOpenSchedule={openScheduleModal}
                  />
                </Col>
              </div>
            )}
          </main>

          {rightSideOpen1 && (
            <aside className="thread-pane is-open">
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  flex: 1,
                }}
              >
                <Col
                  span={24}
                  style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
                >
                  <Row className="px-4 pt-4">
                    <div className="panel-head">
                      <button
                        type="button"
                        className="panel-head-back"
                        title="Back to details"
                        onClick={() => {
                          setRightSideOpen1(false);
                          setRightSideOpen2(true);
                        }}
                      >
                        <IoMdArrowBack size={17} />
                      </button>

                      <div className="panel-head-text">
                        <span className="panel-head-title" title={ThreadDetails?.name}>
                          <span className="thread-hash" aria-hidden="true">
                            #
                          </span>
                          {ThreadDetails?.name}
                        </span>
                        <span
                          className="panel-head-sub cursor-pointer"
                          onClick={() => membersListingModal(ThreadDetails?.members)}
                        >
                          {`${(ThreadDetails?.members?.length || 0) + 1} members · ${Math.min(isMembersThread || 0, (ThreadDetails?.members?.length || 0) + 1)} online`}
                        </span>
                      </div>

                      {ThreadDetails?.start_from_user_id == userData?.id && (
                        <button
                          type="button"
                          className="modal-close"
                          title="Delete thread"
                          onClick={() => setDeleteThreadModal(true)}
                        >
                          <MdOutlineDelete size={16} />
                        </button>
                      )}
                      <button
                        type="button"
                        className="modal-close"
                        title="Close thread"
                        onClick={() => setRightSideOpen1(false)}
                      >
                        <MdClose size={16} />
                      </button>
                    </div>
                  </Row>
                  <div
                    ref={threadScrollingRef}
                    className="thread-stream custom-scrollbar"
                    data-infinite-root
                  >
                    {!threadHistoryExhausted && <div ref={threadSentinelRef} aria-hidden="true" />}
                    {threads_chats_loader && (
                      <div className={`stream-loading ${ThreadsMsgs?.length ? 'is-more' : ''}`}>
                        <Loader.Circular size={ThreadsMsgs?.length ? 24 : 40} />
                      </div>
                    )}
                    {ThreadsMsgs?.map((item, index) => {
                      if (item?.deleted_by_id != null) return null;

                      const mine = item?.sender_id === userData?.id;
                      const sentAt = new Date(item?.createdAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      });

                      // Quoted parent, when this reply points at one.
                      const quoted = item?.reply_id ? (
                        <div
                          style={{ backgroundColor: Colors.White }}
                          className="w-full p-2 border border-[#f5f8ff] rounded-lg mb-2"
                        >
                          <Text.Basic
                            style={{ fontWeight: '800', fontSize: '0.9rem' }}
                            className="text-[#2B3674]"
                            text={
                              SelectedChatGroup?.is_secret
                                ? item?.replied_message?.user?.member_mode
                                : item?.replied_message?.user?.first_name?.replace(
                                    /\b\w/g,
                                    (char) => char.toUpperCase()
                                  )
                            }
                          />
                          {item?.replied_message?.type === 'TEXT' ? (
                            <Text.Basic
                              style={{ fontWeight: '400', fontSize: '0.8rem' }}
                              className="text-[#2B3674]"
                              text={item?.replied_message?.message}
                            />
                          ) : item?.replied_message?.duration ? (
                            <span>
                              <Avatar size={30} shape="square" icon={<GiSoundWaves />} />
                              <Text.Basic
                                style={{ fontWeight: '400', fontSize: '0.8rem' }}
                                className="text-[#2B3674] ml-2"
                                text={'Audio'}
                              />
                            </span>
                          ) : (
                            <span>
                              <Avatar size={20} shape="square" icon={<UserOutlined />} />
                              <Text.Basic
                                style={{ fontWeight: '400', fontSize: '0.8rem' }}
                                className="text-[#2B3674] ml-2"
                                text={'MEDIA'}
                              />
                            </span>
                          )}
                        </div>
                      ) : null;

                      const body =
                        item?.type === 'TEXT' ? (
                          <MessageText
                            style={{ fontWeight: '400', fontSize: '0.8rem' }}
                            className={mine ? 'text-white' : 'text-[#2B3674]'}
                            text={item?.message}
                            matchMentions={matchMentions}
                            selfName={userData?.first_name}
                            onAccent={mine}
                          />
                        ) : (
                          item?.media?.map((media, mediaIndex) =>
                            media?.format?.startsWith('audio/') ? (
                              <ReactAudioPlayer
                                key={media?.id ?? mediaIndex}
                                src={media?.media_url}
                                autoPlay={false}
                                controls
                                onPlay={(e) => handlePlay(e, mediaIndex)}
                                id={mediaIndex}
                              />
                            ) : media?.format?.startsWith('video/') ? (
                              <ReactPlayer
                                key={media?.id ?? mediaIndex}
                                height={180}
                                width={180}
                                url={media?.media_url}
                                controls={true}
                              />
                            ) : media?.format?.startsWith('image/') ? (
                              <Image
                                key={media?.id ?? mediaIndex}
                                className="rounded-xl object-cover"
                                preview={true}
                                width={160}
                                height={160}
                                src={media?.media_url}
                              />
                            ) : null
                          )
                        );

                      // Reaction pills hang under the bubble on the same side it
                      // sits on, so a burst of them cannot straddle the column.
                      const reactions = (
                        <MessageReactions
                          reactions={item?.chat_reactions}
                          currentUserId={userData?.id}
                          isOut={mine}
                          onOpenList={reactedListingModal}
                        />
                      );

                      const picker = (
                        <ReactionPicker
                          emojis={EmojiLists}
                          reactions={item?.chat_reactions}
                          currentUserId={userData?.id}
                          isOpen={currentIndex === item?.id}
                          onToggle={() =>
                            setCurrentIndex(currentIndex === item?.id ? -1 : item?.id)
                          }
                          onClose={() => setCurrentIndex(-1)}
                          onSelect={(emoji) => applyThreadReaction(item, emoji)}
                          align={mine ? 'left' : 'right'}
                          className="shrink-0"
                        />
                      );

                      // Same shape as the main stream: reply / delete / report
                      // live in the hover menu rather than as a standing icon row
                      // under every message.
                      const menu = (
                        <MessageMenu
                          onReply={() => replyMsgThread(item)}
                          onDelete={() => deleteThreadMsg(item, index)}
                          isSaved={Boolean(item?.is_saved)}
                          onToggleSave={item?.deleted_by_id ? null : () => toggleSaveMsg(item)}
                          onReport={mine ? null : () => ReportModalOpen(item)}
                        />
                      );

                      const dayLabel = threadDayLabels.get(item?.id);

                      return (
                        <React.Fragment key={item?.id ?? `idx-${index}`}>
                          <DayDivider label={dayLabel} />
                          {mine ? (
                            <div className="msg-row is-out">
                              {picker}
                              <div className="msg-col">
                                <MessageMenuTooltip placement="leftBottom" menu={menu}>
                                  <div
                                    className="text-end rounded-xl px-4 py-1 relative message-width h-fit msg-bubble"
                                    style={{ backgroundColor: Colors.MainColor }}
                                  >
                                    {quoted}
                                    {body}
                                    <div className="msg-meta is-on-accent">
                                      {sentAt}
                                      {/* A thread is a channel of its own, with its own
                                          membership and its own receipts — so it gets its
                                          own aggregate rather than the parent group's. */}
                                      <MessageTicks
                                        state={
                                          item?.failed
                                            ? 'failed'
                                            : item?.pending
                                              ? 'pending'
                                              : threadOthersReadAt &&
                                                  new Date(threadOthersReadAt) >=
                                                    new Date(item?.createdAt)
                                                ? 'read'
                                                : 'sent'
                                        }
                                      />
                                    </div>
                                  </div>
                                </MessageMenuTooltip>
                                {reactions}
                              </div>
                            </div>
                          ) : (
                            <div className="msg-row">
                              <div className="msg-col">
                                <MessageMenuTooltip placement="rightBottom" menu={menu}>
                                  <div className="rounded-xl bg-[#f5f8ff] px-4 py-2 msg-bubble message-width">
                                    <div className="msg-head">
                                      {SelectedChatGroup?.is_secret ? (
                                        <PlaceholderAvatar
                                          size={28}
                                          variant="anonymous"
                                          seed={`${item?.user?.first_name}${item?.user?.id}`}
                                        />
                                      ) : senderAvatar(item) ? (
                                        <img
                                          className="msg-head-avatar"
                                          src={senderAvatar(item)}
                                          alt=""
                                          width={28}
                                          height={28}
                                          loading="lazy"
                                          decoding="async"
                                        />
                                      ) : (
                                        <PlaceholderAvatar size={28} />
                                      )}
                                      <span className="msg-head-name">
                                        {SelectedChatGroup?.is_secret
                                          ? item?.user?.member_mode
                                          : senderName(item)}
                                      </span>
                                      <span className="msg-head-time">{sentAt}</span>
                                    </div>
                                    {quoted}
                                    {body}
                                  </div>
                                </MessageMenuTooltip>
                                {reactions}
                              </div>
                              {picker}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                    <div ref={lastThreadMessageRef} />
                  </div>
                  <Row className="thread-composer">
                    {replyOnMsgThread && (
                      <Row
                        style={{ backgroundColor: Colors.backGroundColor }}
                        className={`${selectedImagesThread?.length > 0 ? 'mb-16' : ''} w-full`}
                      >
                        <Col span={23} className="mt-3 h-16">
                          <div
                            style={{ backgroundColor: Colors.White }}
                            className="p-2 border-l-4 border-l-[#f56469]"
                          >
                            <Col span={24}>
                              <Text.Basic
                                style={{
                                  fontWeight: '600',
                                  fontSize: '0.8rem',
                                }}
                                className={isDarkMode ? 'text-white' : 'text-[#2B3674]'}
                                text={
                                  SelectedChatGroup?.is_secret
                                    ? replyOnMsgThread?.user?.member_mode
                                    : replyOnMsgThread?.user?.first_name
                                }
                              />
                            </Col>
                            {replyOnMsgThread?.type == 'TEXT' ? (
                              <Text.Basic
                                style={{
                                  fontWeight: '400',
                                  fontSize: '0.8rem',
                                }}
                                className={isDarkMode ? 'text-white' : 'text-[#2B3674]'}
                                text={replyOnMsgThread?.message}
                              />
                            ) : replyOnMsgThread?.type == 'AUDIO' ? (
                              <span>
                                <Avatar size={30} shape="square" icon={<GiSoundWaves />} />
                                <Text.Basic
                                  style={{
                                    fontWeight: '400',
                                    fontSize: '0.8rem',
                                  }}
                                  className="text-[#2B3674] ml-2"
                                  text={'Audio'}
                                />
                              </span>
                            ) : (
                              <span>
                                <Avatar size={20} shape="square" icon={<UserOutlined />} />
                                <Text.Basic
                                  style={{
                                    fontWeight: '400',
                                    fontSize: '0.8rem',
                                  }}
                                  className="text-[#2B3674] ml-2"
                                  text={'MEDIA'}
                                />
                              </span>
                            )}
                          </div>
                        </Col>
                        <Col span={1} className="reply-cancel">
                          <MdCancel
                            size={20}
                            style={{ color: Colors.Black }}
                            className="cursor-pointer"
                            onClick={() => setReplyOnMsgThread(null)}
                          />
                        </Col>
                      </Row>
                    )}

                    <div className="composer-row">
                      <ComposerEmojiPicker
                        onSelect={handleThreadEmojiInsert}
                        disabled={
                          selectedImagesThread.length > 0 ||
                          Boolean(audioUrlThread) ||
                          isRecordingThread
                        }
                      />
                      <Input
                        maxLength={SelectedChatGroup?.is_secret && 3000}
                        onPressEnter={
                          canSendThread && !uploadThreadLoader && !isRecordingThread
                            ? sendThread
                            : undefined
                        }
                        value={
                          selectedImagesThread.length > 0 || audioUrlThread || isRecordingThread
                            ? ''
                            : threadMessageInput
                        }
                        size="medium"
                        placeholder={
                          // These three read the THREAD's own state. They were pointed at
                          // the group composer's `selectedImages` / `audioUrl` /
                          // `isRecording`, so attaching an image or recording a note in
                          // the group changed the placeholder in the thread reply box
                          // while the thread itself was empty.
                          selectedImagesThread.length > 0
                            ? 'Press the send button to send images'
                            : audioUrlThread
                              ? 'Press the send button to send audio'
                              : isRecordingThread
                                ? 'Recording...'
                                : 'Type Thread'
                        }
                        prefix={isRecordingThread ? formatTime(elapsedThreadTime) : ''}
                        onChange={handleThreadMessageInput}
                        suffix={threadSuffix}
                        style={{
                          background: 'var(--panel-2)',
                          borderRight: '1.5px solid var(--border)',
                          borderLeft: '1.5px solid var(--border)',
                          borderBottom: '1.5px solid var(--border)',
                          borderTop: '1.5px solid var(--border)',
                          borderRadius: 'var(--composer-r)',
                        }}
                        className="background-none"
                        disabled={selectedImagesThread.length > 0 || audioUrlThread}
                      />
                      {threadSendAction}
                    </div>
                    <input
                      id="fileInputThread"
                      type="file"
                      style={{ display: 'none' }}
                      accept="image/*"
                      multiple
                      onChange={handleFileInputChangeThread}
                    />
                    <ComposerAttachments
                      images={selectedImagesThread}
                      onRemoveImage={handleRemoveImageThread}
                      onClearImages={() => setSelectedImagesThread([])}
                      audio={audioUrlThread}
                      onRemoveAudio={() => handleRemoveAudioThread(audioUrlThread)}
                      recordedSeconds={elapsedThreadTime}
                      busy={uploadThreadLoader}
                    />
                  </Row>
                </Col>
              </div>
            </aside>
          )}

          {rightSideOpen2 && (
            // The panel is the scroller, so shared-media paging listens here.
            // The inner wrapper carried `class` (not `className`), so React
            // dropped it — its height/overflow never applied and this handler
            // never fired.
            <aside className="info-panel is-open">
              <InfoHeader onClose={() => setRightSideOpen2(false)} />

              <InfoBody onScroll={handleMediaScroll}>
                <InfoIdentity
                  avatar={
                    SelectedChatGroup?.media?.media_url ? (
                      <Image
                        className="avatar-circle object-cover"
                        preview={false}
                        width={76}
                        height={76}
                        src={SelectedChatGroup?.media?.media_url}
                      />
                    ) : (
                      <Avatar size={76} icon={<UserOutlined />} />
                    )
                  }
                  name={SelectedChatGroup?.name?.replace(/\b\w/g, (char) => char.toUpperCase())}
                  status={
                    // Same two numbers as the header, so the panel and the title cannot
                    // disagree. This read `GroupDetails?.member_count` while the header read
                    // `SelectedChatGroup?.member_count` — two different objects, refreshed
                    // by two different requests.
                    <InfoStatusPill online={detailsOnlineCount > 0}>
                      {`${detailsMemberCount} members · ${detailsOnlineCount} online`}
                    </InfoStatusPill>
                  }
                />

                <InfoActions>
                  {/* Calls are unavailable in a broadcast channel — see the header. */}
                  {!isChannel && (
                    <>
                      <InfoAction
                        icon={<IoCallOutline size={17} />}
                        label="Call"
                        onClick={makeVoiceCall}
                      />
                      <InfoAction
                        icon={<CiVideoOn size={18} />}
                        label="Video"
                        onClick={makeVideoCall}
                      />
                    </>
                  )}
                  {/* Editing needs the OWNER/ADMIN role the server checks, not the
                      creator id: `start_from_user_id` misses a promoted admin, and after
                      an ownership transfer it pointed at the wrong person entirely. The
                      creator-id fallback that used to sit beside this check was the same
                      bug from the other side — a creator who handed ownership over and
                      was demoted still saw Edit, and `updateGroup` refused them. */}
                  {canAdministerChannel && (
                    <InfoAction
                      icon={<MdEdit size={16} />}
                      label="Edit"
                      onClick={() => setEditGroup(true)}
                    />
                  )}
                </InfoActions>

                {SelectedChatGroup?.description && (
                  <InfoField label="Description">
                    {/* Read-only: editing lives behind the Edit action, and the
                        old markup used an editable <textarea> that silently
                        discarded anything typed into it. */}
                    <InfoReadout multiline>{SelectedChatGroup.description}</InfoReadout>
                  </InfoField>
                )}

                {GroupDetails?.url && (
                  <InfoField label="Invite link">
                    <InfoReadout
                      action={
                        <button
                          type="button"
                          className="info-readout-action"
                          title="Copy invite link"
                          aria-label="Copy invite link"
                          onClick={() => copyToClipboard(SelectedChatGroup?.url)}
                        >
                          <MdContentCopy size={15} />
                        </button>
                      }
                    >
                      <span
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 11.5,
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={GroupDetails.url}
                      >
                        {GroupDetails.url}
                      </span>
                    </InfoReadout>
                  </InfoField>
                )}

                {/* Threads live here rather than on a route of their own —
                    picking one swaps the details panel for the thread. */}
                <InfoSection
                  label="Threads"
                  count={GroupThreads.length}
                  open={showHideThreads}
                  onToggle={() => setShowHideThreads(!showHideThreads)}
                >
                  {GroupThreads.length === 0 ? (
                    <InfoEmpty>
                      {/* Don't tell someone to start a thread where threads are refused.
                          A channel keeps the section (threads created before it became a
                          channel are still readable) but not the invitation. */}
                      {isChannel
                        ? 'Threads are turned off in this channel.'
                        : 'No threads yet. Reply in a thread from any message to start one.'}
                    </InfoEmpty>
                  ) : (
                    GroupThreads.map((thread) => {
                      const unread = Number(thread?.chat_member?.unread_message_count) || 0;
                      const lastChat = thread?.chats?.[0];

                      return (
                        <div
                          key={thread?.id}
                          role="button"
                          tabIndex={0}
                          className={`thread-row ${
                            ThreadDetails?.id === thread?.id && rightSideOpen1 ? 'is-active' : ''
                          }`}
                          onClick={() => openThread(thread?.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openThread(thread?.id);
                            }
                          }}
                        >
                          <span className="thread-row-glyph">
                            <MdOutlineForum size={15} />
                          </span>
                          <span className="thread-row-text">
                            <span className="thread-row-name">
                              <span className="thread-hash" aria-hidden="true">
                                #
                              </span>
                              {thread?.name || 'Untitled thread'}
                            </span>
                            <span className="thread-row-preview">
                              {lastChat?.deleted_by_id
                                ? 'Message deleted'
                                : lastChat?.type === 'MEDIA'
                                  ? 'Media'
                                  : lastChat?.message || 'No messages yet'}
                            </span>
                          </span>
                          {unread > 0 && <span className="thread-row-badge">{unread}</span>}
                        </div>
                      );
                    })
                  )}
                </InfoSection>

                <InfoSection
                  label="Members"
                  count={GroupDetails?.member_count ?? GroupDetails?.members?.length ?? 0}
                  open={showHideMembers}
                  onToggle={() => setShowHideMembers(!showHideMembers)}
                  /* `addUsersInGroup` requires OWNER/ADMIN on the server, so an ordinary
                     member who pressed this got as far as picking people and submitting
                     before a 400 came back — and the modal closes without waiting for the
                     result, so the refusal was all but invisible. `can_administer` is the
                     server's own answer to this question and is false until GroupDetails
                     loads, which fails closed. The creator-id fallback used by Edit is
                     deliberately not repeated here: a creator who transferred ownership
                     and was demoted can no longer add anyone. */
                  action={
                    canAdministerChannel ? (
                      <InfoButton
                        tone="accent"
                        icon={<MdPersonAddAlt1 size={16} />}
                        onClick={() => addMembersModal()}
                      >
                        Add members
                      </InfoButton>
                    ) : null
                  }
                >
                  {isSecretGroup ? null : !roster.length ? (
                    <InfoEmpty>No members to show.</InfoEmpty>
                  ) : (
                    roster.map((person) => (
                      <MemberRow
                        key={person.member_id}
                        avatar={
                          <span style={{ position: 'relative', flex: '0 0 auto', display: 'flex' }}>
                            {person?.user?.media?.media_url ? (
                              <Image
                                className="avatar-circle"
                                src={person?.user?.media?.media_url}
                                width={34}
                                height={34}
                                preview={false}
                              />
                            ) : (
                              <PlaceholderAvatar size={34} />
                            )}
                            {/* Live where the subscription has an answer, falling back to
                                the status the roster read stamped on the row. It was the
                                HTTP value alone, so a member's dot was frozen at whatever
                                it was when the panel loaded. */}
                            {memberStatus(person) && (
                              <span
                                className={`presence-dot ${
                                  memberStatus(person) === PRESENCE.ONLINE
                                    ? 'is-online'
                                    : 'is-offline'
                                }`}
                                style={{
                                  position: 'absolute',
                                  right: -1,
                                  bottom: -1,
                                  width: 11,
                                  height: 11,
                                  border: '2px solid var(--panel-2)',
                                }}
                              />
                            )}
                          </span>
                        }
                        name={[memberDisplayName(person), person?.is_self ? '(You)' : null]
                          .filter(Boolean)
                          .join(' ')}
                        meta={
                          /*
                            The role comes from the membership row, not from
                            `start_from_user_id`: that field is the creator, so it
                            missed every promoted admin and pointed at the wrong
                            person after an ownership transfer. It also compared
                            against `person.user_id`, which this payload has never
                            carried — the field is `member_id` — so the Owner label
                            never rendered for anyone.
                          */
                          person?.member_role === MEMBER_ROLE.OWNER
                            ? 'Owner'
                            : person?.member_role === MEMBER_ROLE.ADMIN
                              ? 'Admin'
                              : null
                        }
                        trailing={
                          <MemberActions
                            permissions={memberPermissions(person)}
                            name={memberDisplayName(person)}
                            onAction={(kind) => setMemberAction({ kind, person })}
                          />
                        }
                      />
                    ))
                  )}

                  {/* The roster pages on scroll. It used to render whatever the group
                      payload happened to carry — the server bounds that at 200 — as
                      though it were the whole membership, so member 201 of a large
                      channel was invisible to the admin deciding who to remove, with
                      nothing on screen saying the list was partial. */}
                  {!isSecretGroup && !rosterExhausted && roster.length > 0 && (
                    <div ref={rosterSentinelRef} aria-hidden="true" />
                  )}
                </InfoSection>

                <InfoSection
                  label="Shared media"
                  count={shareMedia?.length ?? 0}
                  open={showHideMedia}
                  onToggle={() => setShowHideMedia(!showHideMedia)}
                >
                  <MediaGrid
                    loading={shareMediaLoading && !shareMedia?.length}
                    isEmpty={!shareMedia?.length}
                    emptyText="Nothing shared in this group yet."
                  >
                    {shareMedia?.map((item, mediaIndex) => (
                      <MediaTile
                        key={item?.id ?? mediaIndex}
                        label={
                          item?.format?.startsWith('audio/')
                            ? 'AUDIO'
                            : item?.format?.startsWith('video/')
                              ? 'VIDEO'
                              : null
                        }
                      >
                        {item?.format?.startsWith('image/') ? (
                          <Image
                            src={item?.media_thumb_url}
                            preview={{ src: item?.media_url }}
                            loading="lazy"
                            decoding="async"
                          />
                        ) : item?.format?.startsWith('audio/') ? (
                          <FaAudioDescription size={26} />
                        ) : item?.format?.startsWith('video/') ? (
                          <video src={item?.media_url} muted playsInline />
                        ) : null}
                      </MediaTile>
                    ))}
                  </MediaGrid>
                </InfoSection>

                <InfoButton tone="danger" onClick={() => handleLeaveGroup()}>
                  Leave group
                </InfoButton>
              </InfoBody>
            </aside>
          )}
        </>
      )}

      {/* Report Msg Modal */}
      <Row className="" justify={'center'} align={'center'}>
        <Col span={24} className="flex justify-center">
          <CustomModal
            open={modal}
            cancelModal={() => EditModalClose()}
            className={'custom-modal'}
            centered={true}
            destroyOnClose={true}
            width={450}
            Children={
              <>
                <div className={`flex justify-between w-full items-center mb-6`}>
                  <IoChevronBackCircle color={Colors.White} size={30} />
                  <Text.Basic
                    className="flex-auto text-center text-[#2B3674]"
                    style={{ fontWeight: '600' }}
                    text={'Report Message'}
                  />
                  <MdCancel
                    className="cursor-pointer"
                    color={Colors.ModalCancelColor}
                    size={30}
                    onClick={() => EditModalClose()}
                  />
                </div>
                <form onSubmit={handleSubmitReport(onSubmitFirstModal)} className="w-11/12 mx-auto">
                  <label className="text-[#2B3674]">Report Description</label>
                  <Controller
                    name="description"
                    control={controlReport}
                    // Same rules as the 1:1 report — RHF had none, so the error
                    // paragraph under the field could never render. Counted
                    // trimmed, matching the backend's own check.
                    rules={{
                      validate: (value) => {
                        const text = (value ?? '').trim();
                        if (!text) return 'Describe what is wrong with this message';
                        if (text.length < REPORT_LIMITS.description.min) {
                          return `Give us at least ${REPORT_LIMITS.description.min} characters — "spam" alone is not actionable`;
                        }
                        return (
                          text.length <= REPORT_LIMITS.description.max ||
                          `Keep it under ${REPORT_LIMITS.description.max} characters`
                        );
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <Col span={24} className="relative mb-2">
                        <textarea
                          required
                          maxLength={REPORT_LIMITS.description.max}
                          rows={4}
                          style={{ background: 'var(--panel-2)', resize: 'none' }}
                          className="w-full px-2 py-3 rounded outline-0"
                          {...field}
                          value={field.value ?? ''}
                        />
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 10,
                            marginTop: 4,
                          }}
                        >
                          <span style={{ color: Colors.Error, fontSize: 12 }}>
                            {fieldState.error?.message ?? ''}
                          </span>
                          <span
                            style={{
                              fontSize: 11.5,
                              fontWeight: 600,
                              fontVariantNumeric: 'tabular-nums',
                              color: 'var(--muted)',
                            }}
                          >
                            {(field.value ?? '').length} / {REPORT_LIMITS.description.max}
                          </span>
                        </div>
                      </Col>
                    )}
                  />
                  <Row>
                    <input
                      type="submit"
                      value="Report Now"
                      className="w-full mt-6 ml-2 h-11 rounded cursor-pointer hover:opacity-95"
                      style={{
                        backgroundColor: 'var(--accent)',
                        color: Colors.White,
                      }}
                    />
                  </Row>
                </form>
              </>
            }
          />
        </Col>
      </Row>

      {/* Schedule Message */}
      <Row className="" justify={'center'} align={'center'}>
        <Col span={24} className="flex justify-center">
          <CustomModal
            open={ismodalopen}
            cancelModal={() => setIsmodalopen(false)}
            className={'custom-modal'}
            centered={true}
            destroyOnClose={true}
            width={450}
            Children={
              <>
                <Text.Basic
                  className="flex-auto text-center text-[#2B3674] dark:text-white"
                  style={{ fontWeight: '600' }}
                  text={'Schedule Message'}
                />
                <form
                  onSubmit={handleSubmitMessage(onSubmitMessage)}
                  onKeyDown={(e) => checkKeyDown(e)}
                  className="w-11/12	 mx-auto"
                >
                  <label className="text-[#2B3674] dark:text-white">Selected Date</label>
                  <Controller
                    name="selected_date"
                    control={controlMessage}
                    render={({ field, fieldState }) => (
                      <Col span={24} className="relative mb-2">
                        <input
                          onClick={(e) => e.target.showPicker()}
                          required
                          type="date"
                          placeholder="Select a Date"
                          min={new Date().toISOString().split('T')[0]} // sets today's date as the minimum
                          style={{
                            cursor: 'pointer',
                            border: fieldState.error?.message
                              ? `1px solid ${Colors.MainColor}`
                              : `1px solid ${isDarkMode ? 'var(--border)' : 'var(--border)'}`,
                            background: isDarkMode ? 'var(--panel-2)' : Colors.LightWhite,
                            color: isDarkMode ? 'white' : 'inherit',
                          }}
                          className="w-full px-2 py-3 rounded outline-0"
                          {...field}
                        />
                        {fieldState.error && (
                          <span className="text-red-500">{fieldState.error.message}</span>
                        )}
                      </Col>
                    )}
                  />
                  <label className="text-[#2B3674] dark:text-white">Selected Time</label>
                  <Controller
                    name="selected_time"
                    control={controlMessage}
                    render={({ field, fieldState }) => (
                      <Col span={24} className="relative mb-2">
                        <input
                          onClick={(e) => e.target.showPicker()}
                          required
                          type="time"
                          placeholder="Select Time"
                          step="60" // step in seconds, here = 1 minute increments
                          style={{
                            cursor: 'pointer',
                            border: fieldState.error?.message
                              ? `1px solid ${Colors.MainColor}`
                              : `1px solid ${isDarkMode ? 'var(--border)' : 'var(--border)'}`,
                            background: isDarkMode ? 'var(--panel-2)' : Colors.LightWhite,
                            color: isDarkMode ? 'white' : 'inherit',
                          }}
                          className="w-full px-2 py-3 rounded outline-0"
                          {...field}
                        />
                        {fieldState.error && (
                          <span className="text-red-500">{fieldState.error.message}</span>
                        )}
                      </Col>
                    )}
                  />
                  <label className="text-[#2B3674] dark:text-white">Message</label>
                  <Controller
                    name="selected_message"
                    control={controlMessage}
                    render={({ field, fieldState }) => (
                      <Col span={24} className="relative mb-2">
                        <textarea
                          required
                          placeholder="Type message here"
                          style={{
                            border: fieldState.error?.message
                              ? `1px solid ${Colors.MainColor}`
                              : `1px solid ${isDarkMode ? 'var(--border)' : 'var(--border)'}`,
                            background: isDarkMode ? 'var(--panel-2)' : Colors.LightWhite,
                            color: isDarkMode ? 'white' : 'inherit',
                          }}
                          className="w-full px-2 py-3 rounded outline-0"
                          {...field}
                        />
                        {fieldState.error && (
                          <span className="text-red-500">{fieldState.error.message}</span>
                        )}
                      </Col>
                    )}
                  />

                  <Row>
                    <Col span={12}>
                      <input
                        type="submit"
                        value="Cancel"
                        className="w-11/12 mt-6 mr-2 h-11 rounded cursor-pointer hover:opacity-95"
                        style={{
                          backgroundColor: isDarkMode ? 'var(--panel-2)' : Colors.White,
                          color: isDarkMode ? 'white' : 'var(--accent)',
                          borderColor: isDarkMode ? 'var(--border)' : 'var(--accent)',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                        }}
                        onClick={() => {
                          setIsmodalopen(false);
                          resetMessage();
                        }}
                      />
                    </Col>
                    <Col span={12}>
                      <button
                        disabled={loader}
                        type="submit"
                        className="w-11/12 mt-6 ml-2 h-11 rounded cursor-pointer hover:opacity-95"
                        style={{
                          backgroundColor: Colors.MainColor,
                          color: Colors.White,
                        }}
                      >
                        {loader ? (
                          <div className="flex items-center justify-center">
                            {/* currentColor: the button is brand-filled, so the
                                brand-coloured default spinner cannot be seen. */}
                            <Loader.Circular size={'medium'} color="currentColor" />
                            <span className="ml-2">Save</span>
                          </div>
                        ) : (
                          'Save'
                        )}
                      </button>
                    </Col>
                  </Row>
                </form>
              </>
            }
          />
        </Col>{' '}
      </Row>

      {/* Contact Support Modal  */}
      <Row className="" justify={'center'} align={'center'}>
        <Col span={24} className="flex justify-center">
          <CustomModal
            open={membersListModal}
            cancelModal={() => MembersListClose()}
            className={'custom-modal'}
            centered={true}
            destroyOnClose={true}
            width={450}
            // title={<p className='text-center text-[#2B3674]'>Change Password</p>}
            Children={
              <>
                <div className={`flex items-center justify-center w-full mb-3`}>
                  <Text.Basic
                    className="text-center text-[#2B3674]"
                    style={{ fontWeight: '600' }}
                    text={'Thread Members'}
                  />
                </div>
                {/* {membersList?.length > 0 && ( */}
                <ul className=" rounded-lg w-full">
                  <li style={{ cursor: 'pointer', padding: '5px 0' }}>
                    <div className="my-members-scroll">
                      {membersList?.map((user) => (
                        //  <div className="my-2 flex">
                        //  {user?.user?.media?.media_url ? (<Image preview={false} src={user?.user?.media?.media_url} className="mr-2 rounded-full" width={30} height={30} />) : (<PlaceholderAvatar size={32} />)}
                        //  <div className="flex items-center ml-4"><Text.Basic className='text-center text-[#2B3674]' style={{ fontWeight: '200' }} text={user?.user?.first_name} /></div>
                        //  </div>
                        <Row
                          className="py-2 px-4 mb-1 flex items-center"
                          style={{ background: 'var(--panel-2)' }}
                        >
                          <div className="flex items-center justify-center">
                            {SelectedChatGroup?.is_secret ? (
                              <PlaceholderAvatar
                                size={40}
                                variant="anonymous"
                                seed={`${user?.user?.first_name}${user?.user?.id}`}
                              />
                            ) : user?.user?.media?.media_url ? (
                              <Image
                                className="avatar-circle object-cover"
                                width={40}
                                height={40}
                                src={user?.user?.media?.media_url}
                                preview={false}
                              />
                            ) : (
                              <PlaceholderAvatar size={40} />
                            )}
                          </div>
                          <Text.Basic
                            style={{ fontWeight: '800', fontSize: '0.8rem' }}
                            className="text-[#2B3674] ml-2"
                            text={
                              SelectedChatGroup?.is_secret
                                ? user?.user?.member_mode
                                : user?.user?.first_name?.replace(/\b\w/g, (char) =>
                                    char.toUpperCase()
                                  )
                            }
                          />
                        </Row>
                      ))}
                    </div>
                  </li>
                </ul>
                {/* )} */}
              </>
            }
          />
        </Col>
      </Row>
      {/* Contact Support Modal  */}
      <Row className="" justify={'center'} align={'center'}>
        <Col span={24} className="flex justify-center">
          <CustomModal
            open={reactedListModal}
            cancelModal={() => reactedMembersListClose()}
            className={'custom-modal'}
            centered={true}
            destroyOnClose={true}
            width={450}
            Children={
              <>
                <div className={`flex items-center justify-center w-full mb-3`}>
                  <Text.Basic
                    className="text-center text-[#2B3674]"
                    style={{ fontWeight: '600' }}
                    text={'Total Reactions'}
                  />
                </div>
                <ul className=" rounded-lg w-full">
                  <li style={{ cursor: 'pointer', padding: '5px 0' }}>
                    <div className="my-members-scroll">
                      {reactedMembersList?.map((user) => (
                        <Row
                          className="py-2 px-4 mb-1 flex items-center"
                          style={{ background: 'var(--panel-2)' }}
                        >
                          <div className="flex items-center justify-center">
                            {SelectedChatGroup?.is_secret ? (
                              <PlaceholderAvatar
                                size={40}
                                variant="anonymous"
                                seed={`${user?.reactor?.first_name}${user?.reactor?.id}`}
                              />
                            ) : user?.emoji_id ? (
                              <span className="text-3xl leading-none">{user?.emoji_id}</span>
                            ) : (
                              <PlaceholderAvatar size={40} />
                            )}
                          </div>
                          <Text.Basic
                            style={{ fontWeight: '800', fontSize: '0.8rem' }}
                            className="text-[#2B3674] ml-2"
                            text={
                              SelectedChatGroup?.is_secret
                                ? user?.reactor?.member_mode
                                : user?.reactor?.first_name?.replace(/\b\w/g, (char) =>
                                    char.toUpperCase()
                                  )
                            }
                          />
                        </Row>
                      ))}
                    </div>
                  </li>
                </ul>
              </>
            }
          />
        </Col>
      </Row>

      <Row className="" justify={'center'} align={'center'}>
        <Col span={24} className="flex justify-center">
          <CustomModal
            open={deleteThreadModal}
            cancelModal={() => setDeleteThreadModal(false)}
            className={'custom-modal'}
            centered={true}
            destroyOnClose={true}
            width={450}
            Children={
              <>
                <div className={`flex justify-between w-full items-center mb-6`}>
                  <IoChevronBackCircle
                    className="cursor-pointer"
                    color={Colors.White}
                    size={30}
                    onClick={() => setDeleteThreadModal(false)}
                  />
                  <Text.Basic
                    className="flex-auto text-center text-[#2B3674]"
                    style={{ fontWeight: '600' }}
                    text={'Delete Thread'}
                  />
                  <MdCancel
                    className="cursor-pointer"
                    color={Colors.ModalCancelColor}
                    size={20}
                    onClick={() => setDeleteThreadModal(false)}
                  />
                </div>

                <Text.Basic
                  className="flex-auto text-center text-[#2B3674]"
                  style={{ fontWeight: '400', fontSize: '16px' }}
                  text={`Are you sure you want to delete ${ThreadDetails?.name} thread`}
                />

                <Row className="w-full">
                  <Col span={12}>
                    <input
                      type="button"
                      value="No"
                      className="w-11/12 mt-6 mr-2 h-11 rounded cursor-pointer hover:opacity-95"
                      style={{
                        backgroundColor: Colors.White,
                        color: 'var(--accent)',
                        borderColor: 'var(--accent)',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                      }}
                      onClick={() => setDeleteThreadModal(false)}
                    />
                  </Col>
                  <Col span={12}>
                    <input
                      type="submit"
                      value="Yes"
                      className="w-11/12 mt-6 ml-2 h-11 rounded cursor-pointer hover:opacity-95"
                      style={{
                        backgroundColor: 'var(--accent)',
                        color: Colors.White,
                      }}
                      onClick={() => deleteThread(ThreadDetails)}
                    />
                  </Col>
                </Row>
              </>
            }
          />

          {/* Delete for everyone. `tone="danger"` rather than the accent: the brand is
              red, so an accent-filled button here would read as the ordinary primary
              action on something that cannot be undone. */}
          <ConfirmDialog
            open={Boolean(retractTarget)}
            title={'Delete for everyone'}
            tone={'danger'}
            confirmText={'Delete for everyone'}
            cancelText={'Cancel'}
            onCancel={() => setRetractTarget(null)}
            onConfirm={confirmRetract}
            body={
              <>
                <div>Delete this message for everyone in the channel?</div>
                {retractTarget?.message && (
                  <div
                    className="mt-3 px-3 py-2 text-left"
                    style={{
                      background: 'var(--panel-2)',
                      borderRadius: '8px',
                      fontSize: '13.5px',
                      color: 'var(--muted)',
                      maxHeight: '96px',
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                    }}
                  >
                    {retractTarget.message}
                  </div>
                )}
                <div className="mt-3" style={{ fontSize: '13px', color: 'var(--muted)' }}>
                  It disappears for every member, along with its reactions, attachments and
                  anyone&apos;s saved copy. Notifications already delivered to phones cannot be
                  recalled.
                </div>
              </>
            }
          />
        </Col>
      </Row>

      <Row className="" justify={'center'} align={'center'}>
        <Col span={24} className="flex justify-center">
          <CustomModal
            open={addMembersModalOpen}
            cancelModal={() => EditModalClose()}
            className={'custom-modal'}
            centered={true}
            destroyOnClose={true}
            width={450}
            Children={
              <>
                <div className={`flex justify-between w-full items-center mb-6`}>
                  <Text.Basic
                    className="flex-auto text-center text-[#2B3674] dark:text-white"
                    style={{ fontWeight: '600' }}
                    text={'Add People'}
                  />
                  <MdCancel
                    className="cursor-pointer"
                    color={Colors.ModalCancelColor}
                    size={30}
                    onClick={() => setAddMembersModalOpen(false)}
                  />
                </div>

                <form className="w-11/12 mx-auto" onSubmit={handleSubmit(onSubmitAddMembersModal)}>
                  <label className="text-[#2B3674] dark:text-white">Add via Employee ID</label>
                  <Col span={24} className="relative mb-2">
                    {/* Numeric keypad without `type="number"`'s spinner, scroll
                        wheel and inert `maxLength` — the value is digits-only and
                        capped in `searchHandler`. */}
                    <input
                      placeholder={`Enter ${LOGIN_ID_DIGITS}-digit Employee ID`}
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={LOGIN_ID_DIGITS}
                      value={inputValue}
                      onChange={searchHandler}
                      style={{ fontFamily: 'var(--font-mono)' }}
                      className="w-full px-2 py-3 rounded outline-0 bg-[#eef9fe] dark:bg-[#2a2a2a] text-black dark:text-white"
                    />
                  </Col>

                  {!group_loader ? (
                    AddUserById && inputValue?.length === LOGIN_ID_DIGITS ? (
                      <Row className="p-2 mb-1 mt-2 bg-[#EEF9FE] dark:bg-[#353535]">
                        <Col
                          xxl={4}
                          xl={4}
                          lg={6}
                          md={2}
                          sm={3}
                          xs={4}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {AddUserById?.media?.media_url ? (
                            <Image
                              className="avatar-circle object-cover"
                              height={50}
                              width={50}
                              src={AddUserById?.media?.media_url}
                              preview={false}
                            />
                          ) : (
                            <PlaceholderAvatar size={40} />
                          )}
                        </Col>
                        <Col xxl={18} xl={18} lg={16} md={19} sm={18} xs={16}>
                          <Row className="ml-1">
                            <Col span={24}>
                              <Text.Basic
                                style={{
                                  fontWeight: '800',
                                  fontSize: '0.8rem',
                                }}
                                className="text-[#2B3674] dark:text-white"
                                text={AddUserById?.first_name}
                              />
                            </Col>
                            {/* <Col span={24} style={{ marginTop: '4px' }}>
                                                        <Row>
                                                            <Text.Basic style={{ fontWeight: '400', fontSize: '0.6rem' }} className="text-[#2B3674]" text="Message content" />
                                                        </Row>
                                                    </Col> */}
                          </Row>
                        </Col>
                        <Col
                          xxl={2}
                          xl={2}
                          lg={2}
                          md={3}
                          sm={3}
                          xs={4}
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Checkbox
                            value={isChecked}
                            onChange={(e) => handleCheckboxChange(e, AddUserById)}
                            checked={selectedPeople.some((p) => p.id === AddUserById.id)}
                          />
                        </Col>
                      </Row>
                    ) : inputValue?.length === LOGIN_ID_DIGITS ? (
                      /* Replaces the "User Not Found" toast the search saga used to raise on
                         every debounced keystroke. A miss belongs in the panel being searched,
                         not in a notification stack. */
                      <Text.Basic
                        style={{ fontSize: '0.75rem', display: 'block', padding: '10px 4px' }}
                        className="text-[#8F9BBA] dark:text-[#9a9a9a]"
                        text="No employee with that login ID."
                      />
                    ) : null
                  ) : (
                    <Loader.Circular size={60} color={Colors.redColor} />
                  )}
                  <label className="text-[#2B3674] dark:text-white">
                    Selected Employees {selectedPeople?.length}
                  </label>
                  {selectedPeople.map((person) => (
                    <Row key={person.id} className="p-2 mb-1 bg-[#EEF9FE] dark:bg-[#353535]">
                      <Col
                        xxl={4}
                        xl={4}
                        lg={6}
                        md={2}
                        sm={3}
                        xs={4}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {person?.media?.media_url ? (
                          <Image
                            className="avatar-circle"
                            src={person?.media?.media_url}
                            width={40}
                            height={40}
                          />
                        ) : (
                          <PlaceholderAvatar size={40} />
                        )}
                      </Col>
                      <Col xxl={18} xl={18} lg={16} md={19} sm={18} xs={16}>
                        <Row className="ml-1">
                          <Col span={24}>
                            <Text.Basic
                              style={{ fontWeight: '800', fontSize: '0.8rem' }}
                              className="text-[#2B3674] dark:text-white"
                              text={person?.first_name}
                            />
                          </Col>
                          {/* <Col span={24} style={{ marginTop: '4px' }}>
                                                                            <Row>
                                                                                <Text.Basic style={{ fontWeight: '400', fontSize: '0.6rem' }} className="text-[#2B3674]" text={'Online'} />
                                                                            </Row>
                                                                        </Col> */}
                        </Row>
                      </Col>
                      <Col
                        xxl={2}
                        xl={2}
                        lg={2}
                        md={3}
                        sm={3}
                        xs={4}
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Checkbox
                          checked={selectedPeople.some((p) => p.id === person.id)}
                          onChange={(e) => onCheckboxChange(person, e.target.checked)}
                        />
                      </Col>
                    </Row>
                  ))}

                  {/* `> 0`, not the bare length: React renders the number `0` for a falsy
                      numeric test, so an empty selection printed a stray "0" in the modal. */}
                  {selectedPeople.length > 0 && (
                    <Input
                      type="submit"
                      value="Done"
                      className="w-full mt-6 mr-2 h-11 rounded cursor-pointer"
                      style={{
                        backgroundColor: Colors.MainColor,
                        color: Colors.White,
                      }}
                    />
                  )}
                </form>
              </>
            }
          />
        </Col>
      </Row>
      <Row className="" justify={'center'} align={'center'}>
        <Col span={24} className="flex justify-center">
          <CustomModal
            open={editGroup}
            cancelModal={() => EditModalClose()}
            className={'custom-modal'}
            centered={true}
            destroyOnClose={true}
            width={450}
            Children={
              <>
                <div className={`flex justify-between w-full items-center mb-6`}>
                  <Text.Basic
                    className="flex-auto text-center text-[#2B3674] dark:text-white"
                    style={{ fontWeight: '600' }}
                    text={'Edit Group'}
                  />
                  <button
                    type="button"
                    className="modal-close"
                    title="Close"
                    onClick={() => {
                      setEditGroup(false);
                      resetEdit();
                    }}
                  >
                    <MdClose size={16} />
                  </button>
                </div>

                <form onSubmit={handleSubmitEdit(onSubmitEditModal)} className="w-11/12 mx-auto">
                  <div className="upload-photo-container">
                    <label htmlFor="file">
                      <input
                        id="file"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                        required={false}
                      />
                      {imageURL ? (
                        <div className="relative">
                          <img src={imageURL} alt="Uploaded" className="circle-image relative" />
                          <Avatar
                            src={DeleteAccImage}
                            className="left-24 bottom-2 z-10 cursor-pointer absolute"
                            onClick={deleteImgae}
                          />
                        </div>
                      ) : (
                        <div className="cursor-pointer">
                          <img src={GroupImage} width={110} height={110} alt="" />
                        </div>
                      )}
                    </label>
                  </div>
                  <label className="text-[#2B3674] dark:text-white">Group Name</label>
                  <Controller
                    name="groupName"
                    control={controlEdit}
                    /**
                     * Mirrors `validateCreateGroup` on the backend and the create
                     * form in NewConversationModal. Edit had NO rules: a two-letter
                     * name passed the form and came back a 400 from the server.
                     */
                    rules={{
                      validate: (value) => {
                        const text = (value ?? '').trim();
                        if (!text) return 'Name is required';
                        return (
                          (text.length >= GROUP_LIMITS.name.min &&
                            text.length <= GROUP_LIMITS.name.max) ||
                          `Name must be between ${GROUP_LIMITS.name.min} - ${GROUP_LIMITS.name.max} characters`
                        );
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <Col span={24} className="relative mb-2">
                        <input
                          placeholder={SelectedChatGroup?.name}
                          required
                          type={'text'}
                          maxLength={GROUP_LIMITS.name.max}
                          style={{
                            background: isDarkMode ? 'var(--panel-2)' : 'var(--panel-2)',
                            color: isDarkMode ? 'white' : 'inherit',
                          }}
                          className="w-full px-2 py-3 rounded outline-0 bg-[#eef9fe] dark:bg-[#353535]"
                          {...field}
                          value={field.value ?? ''}
                        />
                        <div className="group-field-foot">
                          <span className="group-field-error">
                            {fieldState.error?.message ?? ''}
                          </span>
                          <span className="group-field-count">
                            {(field.value ?? '').trim().length}/{GROUP_LIMITS.name.max}
                          </span>
                        </div>
                      </Col>
                    )}
                  />

                  <label className="text-[#2B3674] dark:text-white">Description</label>
                  <Controller
                    name="description"
                    control={controlEdit}
                    rules={{
                      validate: (value) => {
                        const text = (value ?? '').trim();
                        if (!text) return 'Description is required';
                        return (
                          (text.length >= GROUP_LIMITS.description.min &&
                            text.length <= GROUP_LIMITS.description.max) ||
                          `Description must be between ${GROUP_LIMITS.description.min} - ${GROUP_LIMITS.description.max} characters`
                        );
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <Col span={24} className="relative mb-2">
                        <textarea
                          required
                          rows={3}
                          maxLength={GROUP_LIMITS.description.max}
                          placeholder={SelectedChatGroup?.description}
                          style={{
                            background: isDarkMode ? 'var(--panel-2)' : 'var(--panel-2)',
                            resize: 'none',
                            color: isDarkMode ? 'white' : 'inherit',
                          }}
                          className="w-full px-2 py-3 rounded outline-0 bg-[#eef9fe] dark:bg-[#353535]"
                          {...field}
                          value={field.value ?? ''}
                        />
                        <div className="group-field-foot">
                          <span className="group-field-error">
                            {fieldState.error?.message ?? ''}
                          </span>
                          <span className="group-field-count">
                            {(field.value ?? '').trim().length}/{GROUP_LIMITS.description.max}
                          </span>
                        </div>
                      </Col>
                    )}
                  />

                  {/* Not offered for a broadcast channel: the two are mutually exclusive
                      server-side (a secret channel's reaction fan-out is per-member, which
                      does not survive a broadcast audience), so `updateGroup` refuses the
                      pair. The create modal already disables its own secret switch for the
                      same reason — this was the one route left to a refusal the user could
                      do nothing about. The server check stays as the backstop. */}
                  {!isChannel && (
                    <Row
                      className="px-2 bg-[#EEF9FE] dark:bg-[#353535]"
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Text.Basic
                        className="py-2 tracking-wider text-black dark:text-white"
                        style={{ fontWeight: '400', fontSize: '0.9rem' }}
                        text={'Secret Group ?'}
                      />
                      <img
                        alt=""
                        className="cursor-pointer"
                        src={secretGroupValue ? Notifition3Image : hoverNotificationsImage}
                        onClick={() => setSecretGroupValue(!secretGroupValue)}
                      />
                    </Row>
                  )}

                  {!isChannel && secretGroupValue === true ? (
                    <Row style={{ display: 'flex', alignItems: 'center' }} className="mt-2">
                      <IoInformationCircleSharp className="text-red-500" />
                      <Text.Basic
                        className="tracking-wider text-red-500"
                        style={{
                          fontWeight: '400',
                          fontSize: '0.5rem',
                          paddingTop: '2px',
                        }}
                        text={
                          "Once this is activated, the secret group will have it's own respective features"
                        }
                      />
                    </Row>
                  ) : (
                    ''
                  )}

                  <Row>
                    <Col span={12}>
                      <input
                        type="button"
                        value="Cancel"
                        className="w-11/12 mt-6 mr-2 h-11 rounded cursor-pointer hover:opacity-95"
                        style={{
                          backgroundColor: isDarkMode ? 'var(--panel-2)' : Colors.White,
                          color: isDarkMode ? 'white' : 'var(--accent)',
                          borderColor: isDarkMode ? 'var(--border)' : 'var(--accent)',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                        }}
                        onClick={() => {
                          setEditGroup(false);
                          resetEdit();
                        }}
                      />
                    </Col>
                    <Col span={12}>
                      <input
                        type="submit"
                        value="Edit"
                        className="w-11/12 mt-6 ml-2 h-11 rounded cursor-pointer hover:opacity-95"
                        style={{
                          backgroundColor: 'var(--accent)',
                          color: Colors.White,
                        }}
                      />
                    </Col>
                  </Row>
                </form>
              </>
            }
          />

          <CustomModal
            open={leaveGroupModal}
            cancelModal={() => setLeaveGroupModal(false)}
            className={'custom-modal'}
            centered={true}
            destroyOnClose={true}
            width={450}
            Children={
              <>
                <div className={`flex justify-between w-full items-center mb-6`}>
                  <IoChevronBackCircle
                    className="cursor-pointer"
                    color={Colors.White}
                    size={30}
                    onClick={() => setLeaveGroupModal(false)}
                  />
                  <Text.Basic
                    className="flex-auto text-center text-[#2B3674]"
                    style={{ fontWeight: '600' }}
                    text={'Leave Group'}
                  />
                  <MdCancel
                    className="cursor-pointer"
                    color={Colors.ModalCancelColor}
                    size={20}
                    onClick={() => setLeaveGroupModal(false)}
                  />
                </div>

                <Text.Basic
                  className="flex-auto text-center text-[#2B3674] dark:text-white"
                  style={{ fontWeight: '400', fontSize: '16px' }}
                  text={
                    mustTransferBeforeLeaving
                      ? `You own ${SelectedChatGroup?.name}. Hand ownership to another member first — a group with no owner can never be edited or administered again.`
                      : `Are you sure you want to leave ${SelectedChatGroup?.name} group`
                  }
                />

                <Row className="w-full">
                  <Col span={12}>
                    <input
                      type="button"
                      value={mustTransferBeforeLeaving ? 'Cancel' : 'No'}
                      className="w-11/12 mt-6 mr-2 h-11 rounded cursor-pointer hover:opacity-95"
                      style={{
                        backgroundColor: isDarkMode ? 'var(--panel-2)' : Colors.White,
                        color: isDarkMode ? 'white' : 'var(--accent)',
                        borderColor: isDarkMode ? 'var(--border)' : 'var(--accent)',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                      }}
                      onClick={() => setLeaveGroupModal(false)}
                    />
                  </Col>
                  <Col span={12}>
                    {/* An owner gets the route out, not a button the server will refuse. */}
                    <input
                      type="button"
                      value={mustTransferBeforeLeaving ? 'Choose an owner' : 'Yes'}
                      className="w-11/12 mt-6 ml-2 h-11 rounded cursor-pointer hover:opacity-95"
                      style={{
                        backgroundColor: 'var(--accent)',
                        color: Colors.White,
                      }}
                      onClick={() =>
                        mustTransferBeforeLeaving ? startOwnershipHandover() : confirmLeaveGroup()
                      }
                    />
                  </Col>
                </Row>
              </>
            }
          />

          {/* Promote / demote / transfer / remove, all one prompt: the four differ only
              in what they say and which saga they fire. */}
          <CustomModal
            open={Boolean(memberAction)}
            cancelModal={() => setMemberAction(null)}
            className={'custom-modal'}
            centered={true}
            destroyOnClose={true}
            width={450}
            Children={
              <>
                <div className={`flex justify-between w-full items-center mb-6`}>
                  <IoChevronBackCircle
                    className="cursor-pointer"
                    color={Colors.White}
                    size={30}
                    onClick={() => setMemberAction(null)}
                  />
                  <Text.Basic
                    className="flex-auto text-center text-[#2B3674] dark:text-white"
                    style={{ fontWeight: '600' }}
                    text={MEMBER_ACTIONS[memberAction?.kind]?.title ?? ''}
                  />
                  <MdCancel
                    className="cursor-pointer"
                    color={Colors.ModalCancelColor}
                    size={20}
                    onClick={() => setMemberAction(null)}
                  />
                </div>

                <Text.Basic
                  className="flex-auto text-center text-[#2B3674] dark:text-white"
                  style={{ fontWeight: '400', fontSize: '15px' }}
                  text={
                    MEMBER_ACTIONS[memberAction?.kind]?.body(
                      memberDisplayName(memberAction?.person)
                    ) ?? ''
                  }
                />

                <Row className="w-full">
                  <Col span={12}>
                    <input
                      type="button"
                      value="Cancel"
                      className="w-11/12 mt-6 mr-2 h-11 rounded cursor-pointer hover:opacity-95"
                      style={{
                        backgroundColor: isDarkMode ? 'var(--panel-2)' : Colors.White,
                        color: isDarkMode ? 'white' : 'var(--accent)',
                        borderColor: isDarkMode ? 'var(--border)' : 'var(--accent)',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                      }}
                      onClick={() => setMemberAction(null)}
                    />
                  </Col>
                  <Col span={12}>
                    <input
                      type="button"
                      value={MEMBER_ACTIONS[memberAction?.kind]?.confirmLabel ?? 'Confirm'}
                      className="w-11/12 mt-6 ml-2 h-11 rounded cursor-pointer hover:opacity-95"
                      style={{
                        backgroundColor: MEMBER_ACTIONS[memberAction?.kind]?.danger
                          ? 'var(--danger)'
                          : 'var(--accent)',
                        color: Colors.White,
                      }}
                      onClick={() => runMemberAction()}
                    />
                  </Col>
                </Row>
              </>
            }
          />
        </Col>
      </Row>
    </>
  );
};

export default GroupMessages;
